import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import RemoteCommandBar from '../shottrainer/remoteCommandBar';
import ShotTrainerFieldCompact from '../shottrainer/fieldCompact';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';
import GuiList from '../../components/gui/list';
import UserProfileInline from '../../components/user/profileInline';
import Camera from '../../components/gui/camera';

import ShooterMarkBadges from './shooterMarkBadges';
import ConventionViewInfo from './view-info';
import ConventionNotFound from './notFound';
import ConventionViewSponsors from './view-sponsors';
import ConventionViewStages from './view-stages';
import ConventionViewSignup from './view-signup';
import ConventionPayBottomBar, { countCartItems } from './payBottomBar';
import ConventionViewStageT96 from './view-stage-t96';
import NewsList from '../../components/news/list';
import MemberProducts from './memberProducts';
import ConventionMyPurchases from './myPurchases';
import ConventionMyAnalysis from './myAnalysis';
import ConventionMyNextStage from './myNextStage';
import ConventionWeatherSummary from './weatherSummary';
import ConventionEventPhotos from './eventPhotos';
import ConventionPrizes from './prizes';
import ConventionParticipantsDone from './participantsDone';

import FirebaseConsumer from '../../firebaseConsumer';
import LiveIndicator from './liveIndicator';
import { shouldApplyCommand } from '../../lib/conventionRealtime';

import localUtil from '../../lib/util';
import { getOfficialMark } from '../../lib/shootingMarks';
import { buildConventionRecap } from '../../lib/conventionRecap';
import { getStandMaxValues, getStandInfo, getStandScoreConfig, getStageTimeLabel, getStageTypeDisplay, isConventionMember, isConventionOrganizer, hasMegalinkResults } from '../../lib/conventionResults';
import { megalinkStages, runningMegalinkStages } from '../../lib/pistolTargets';

// Lag types dokkapk-style training evenings add on the spot; offered as outline
// buttons under «Ny <last type>» once the last Lag is Ferdig.
const COMMON_STAGE_TYPES = ['nais', 'hurtig', 'norgesfelt'];
// Recap illustration job polling (see loadRecapImage / pollRecapImageJob).
const RECAP_IMAGE_POLL_INTERVAL_MS = 3000;
const RECAP_IMAGE_POLL_MAX_MS = 180000;
import { getStandMagnumInfo, formatStandMagnumSummary } from '../../lib/conventionCourse';
import { getConventionPayState, requiredStarts } from '../../lib/conventionPayState';
import { resolveConventionTimeState } from '../../lib/conventionTimeState';
import { unreadCount, getLastSeen } from '../../lib/conventionCrewMessages';
import { QRCodeSVG } from 'qrcode.react';
import confetti from 'canvas-confetti';

const INPUT_STYLE = 'width: 45px; min-width: 45px; max-width: 45px; height: 50px; min-height: 50px;font-size: 1.3rem; line-height: 1.2rem;';

// Score celebration sounds
const perfectScoreSound = new Audio('../../assets/sounds/oh-yeah-407752.mp3');
const almostPerfectSound = new Audio('../../assets/sounds/girl-oh-no-150550.mp3');
const applauseSound = new Audio('../../assets/sounds/crowd-applause-113728.mp3');
const lowScoreSound = new Audio('../../assets/sounds/pistol_riccochet-7046.mp3');

const triggerPerfectScoreConfetti = () => {
    confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
    });
    perfectScoreSound.play().catch(err => console.log('Sound play failed:', err));

    // Animated "50" text
    const text = document.createElement('div');
    text.textContent = '50';
    text.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        font-size: 150px;
        font-weight: bold;
        color: gold;
        text-shadow: 3px 3px 0 #ff6600, -3px -3px 0 #ff6600, 3px -3px 0 #ff6600, -3px 3px 0 #ff6600;
        z-index: 10000;
        pointer-events: none;
        animation: pop50 1.5s ease-out forwards;
    `;

    // Add keyframes if not exists
    if (!document.getElementById('pop50-keyframes')) {
        const style = document.createElement('style');
        style.id = 'pop50-keyframes';
        style.textContent = `
            @keyframes pop50 {
                0% { transform: translate(-50%, -50%) scale(0) rotate(-10deg); opacity: 1; }
                20% { transform: translate(-50%, -50%) scale(1.2) rotate(5deg); opacity: 1; }
                40% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; }
                100% { transform: translate(-50%, -50%) scale(1.5) rotate(0deg); opacity: 0; }
            }
        `;
        document.head.appendChild(style);
    }

    document.body.appendChild(text);
    setTimeout(() => text.remove(), 1500);
};

const triggerAlmostPerfectSound = () => {
    almostPerfectSound.play().catch(err => console.log('Sound play failed:', err));

    // Animated Homer D'oh image
    const img = document.createElement('img');
    img.src = '../../assets/images/simpsons-bart-doh.png';
    img.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        height: 250px;
        z-index: 10000;
        pointer-events: none;
        animation: pop49 1.5s ease-out forwards;
    `;

    // Add keyframes if not exists
    if (!document.getElementById('pop49-keyframes')) {
        const style = document.createElement('style');
        style.id = 'pop49-keyframes';
        style.textContent = `
            @keyframes pop49 {
                0% { transform: translate(-50%, -50%) scale(0) rotate(-10deg); opacity: 1; }
                20% { transform: translate(-50%, -50%) scale(1.2) rotate(5deg); opacity: 1; }
                40% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; }
                100% { transform: translate(-50%, -50%) scale(1.5) rotate(0deg); opacity: 0; }
            }
        `;
        document.head.appendChild(style);
    }

    document.body.appendChild(img);
    setTimeout(() => img.remove(), 1500);
};

const triggerScreenShake = () => {
    const body = document.body;
    body.style.transition = 'transform 0.05s ease-in-out';
    const shakes = [
        'translate(-5px, 0)',
        'translate(5px, 0)',
        'translate(-5px, 0)',
        'translate(5px, 0)',
        'translate(-3px, 0)',
        'translate(3px, 0)',
        'translate(0, 0)',
    ];
    let i = 0;
    const shake = () => {
        body.style.transform = shakes[i];
        i++;
        if (i < shakes.length) {
            setTimeout(shake, 50);
        }
    };
    shake();
};

const triggerApplause = () => {
    applauseSound.play().catch(err => console.log('Sound play failed:', err));
};

const triggerLowScoreSound = () => {
    lowScoreSound.play().catch(err => console.log('Sound play failed:', err));
};

const triggerGreenFlash = (element) => {
    if (!element) return;
    element.style.transition = 'background-color 0.1s ease-in-out';
    let count = 0;
    const blink = () => {
        element.style.backgroundColor = count % 2 === 0 ? '#90EE90' : '';
        count++;
        if (count < 4) { // 2 blinks = 4 transitions (on/off/on/off)
            setTimeout(blink, 150);
        }
    };
    blink();
};

const triggerOrangeFlash = (element) => {
    if (!element) return;
    element.style.transition = 'background-color 0.1s ease-in-out';
    let count = 0;
    const blink = () => {
        element.style.backgroundColor = count % 2 === 0 ? '#FFB347' : '';
        count++;
        if (count < 4) {
            setTimeout(blink, 150);
        }
    };
    blink();
};


const MARKDOWN_OPTIONS = {
	pedantic: false,
	gfm: true,
	breaks: true,
	sanitize: false,
	smartLists: true,
	smartypants: true,
	xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

function participantExists(participants, key, value) {
    return participants.some(participant => participant[key] === value);
}

function generateArray(X) {
    const myNewShinyArray = [...Array(parseInt(X, 10) + 1).keys()];
    myNewShinyArray.shift();
    return myNewShinyArray;
}


function displayImage(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3SmallLink}`;
    return imgSrc;
}


@withText(props => ({
    searchTitle: <Text id='input.search-for-user'>Search for user</Text>,
    otpTitle: <Text id='input.otp-input'>Enter OTP</Text>,
}))
@observer
class ConventionView extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            adminToolbarMore: false,
            adminToolbarHeight: 0,
            sections: fields.get('convention'),
            editEnabled: true,
            liveActive: false,
            lastLiveUpdate: 0,
            conventionTab: 'stevne',
            memberProductCount: 0,
            notFound: false,
            recapImageInProgress: false,
        };
        this.mainContainer = null;
        this.countdownTimer = null;
        this.updateContentTimer = null;
        this.updateTimer = {};
        this.saveTimer = {};
        this.refs = {};
        // Track record IDs locally (faster than waiting for store to update)
        this.localResultIds = {};
        // Track pending saves to serialize them
        this.savingPromises = {};

        this.firebaseConsumer = null;
        this.consumerConventionId = null;
        this.consumerReady = false;
        this.consumerWarmupTimer = null;
        this.structuralReloadTimer = null;
        this.handleVisibility = null;
        this.lastResultsLoad = 0;
        // Viewing session tracking (scrollView entries carry the IP for geo stats)
        this.viewingConventionId = null;
        this.viewSessionId = null;
        this.viewStartTime = null;
        this.viewingHeartbeatTimer = null;
        this.handleViewingVisibilityChange = null;
        this.handleViewingBeforeUnload = null;
    }

    // Session tracking for analytics - same pattern as race/trackingMap.js.
    // The scrollView backend attaches ip + user agent and skips admin sessions.
    sendViewingSession = async (isFinal = false) => {
        const conventionId = this.viewingConventionId;
        if (!conventionId || !this.viewStartTime) {
            return;
        }
        const endTime = Date.now();
        const duration = endTime - this.viewStartTime;
        // Only send if duration is at least 1 second
        if (duration < 1000) {
            return;
        }
        const trackingData = [{
            type: 'convention',
            elementId: conventionId,
            time: endTime,
            startTime: this.viewStartTime,
            endTime,
            duration,
            sessionId: this.viewSessionId,
        }];
        try {
            await util.fetchApi('/api/scrollview/', { method: 'POST' }, { data: trackingData });
        } catch (err) {
            // Silently fail - don't disrupt user experience
        }
        if (!isFinal) {
            this.viewStartTime = endTime;
        }
    }

    startViewingSession = (conventionId) => {
        if (this.viewingConventionId === conventionId) {
            return;
        }
        this.stopViewingSession();
        this.viewingConventionId = conventionId;
        this.viewSessionId = `conv_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
        this.viewStartTime = Date.now();

        // Heartbeat every 30 seconds to capture ongoing viewing
        this.viewingHeartbeatTimer = setInterval(() => {
            this.sendViewingSession(false);
        }, 30 * 1000);

        this.handleViewingVisibilityChange = () => {
            if (document.hidden) {
                this.sendViewingSession(false);
            } else {
                this.viewStartTime = Date.now();
            }
        };
        document.addEventListener('visibilitychange', this.handleViewingVisibilityChange);

        this.handleViewingBeforeUnload = () => {
            this.sendViewingSession(true);
        };
        window.addEventListener('beforeunload', this.handleViewingBeforeUnload);
    }

    stopViewingSession = () => {
        if (this.viewingHeartbeatTimer) {
            clearInterval(this.viewingHeartbeatTimer);
            this.viewingHeartbeatTimer = null;
        }
        if (this.handleViewingVisibilityChange) {
            document.removeEventListener('visibilitychange', this.handleViewingVisibilityChange);
            this.handleViewingVisibilityChange = null;
        }
        if (this.handleViewingBeforeUnload) {
            window.removeEventListener('beforeunload', this.handleViewingBeforeUnload);
            this.handleViewingBeforeUnload = null;
        }
        if (this.viewingConventionId) {
            this.sendViewingSession(true);
        }
        this.viewingConventionId = null;
        this.viewSessionId = null;
        this.viewStartTime = null;
    }

    addRef = (key, node) => {
        if (node) {
            this.refs[key] = node;
        }
    }

    resetSummary = () => {
        const { conventionStore } = this.props.stores;
        // Reset the summary object
        conventionStore.updateKeyValue('summary', {});
    }

    loadAll = async (props = this.props, emptyFirst = false) => {
        const { id } = props;
        const { conventionStore, userStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { user } = userStore;
        if (emptyFirst) {
            conventionStore.updateKeyValue('convention', {});
            // Clear the previous verdict along with the previous convention, so
            // switching id (componentWillReceiveProps) from a not-found stevne to a
            // valid one does not flash the stale not-found panel while it loads.
            // Only mount and id-change pass emptyFirst, so the 60s reload and the
            // visibilitychange reload never clear a standing verdict.
            if (this.state.notFound) {
                this.setState({ notFound: false });
            }
        }
        conventionStore.setRefreshPage(() => {
            this.loadAll();
        });

        await conventionStore.loadClassMap();
        await conventionStore.loadDisciplineMap();

        await conventionStore.load(id, false, {
            query: {
                hostname: conventionStore.currentHostname,
            },
            addData: ['tags', 'participants'],
        });
        const { convention } = conventionStore;
        // An empty load result means the stevne is deleted (`offline: 1`), outside
        // this domain's customer scope, or not visible to this user. Decide it here,
        // right after OUR OWN load, so the flag always refers to the id this
        // component asked for — conventionStore.load(id) is also called by
        // listCompact, stageSelector, stagesKanban, edit, index and weaponPermit,
        // so a store-level flag could be flipped by a load for a different id.
        // Only setState on a change: loadAll also runs on a 60s timer and on
        // visibilitychange. A stevne deleted while it is open flips to the panel on
        // the next reload, which is what we want.
        const notFound = !convention || !convention.id;
        if (notFound !== this.state.notFound) {
            this.setState({ notFound });
        }
        if (notFound) {
            return;
        }
        this.setupLiveConsumer(convention.id);
        await conventionStore.getJwt(convention.uuidv4);
        await this.loadResults();
        await this.loadNews();

        // Track convention view
        const { appState } = this.props.stores;
        if (convention?.id) {
            appState.trackConvention('view', convention);
            this.startViewingSession(convention.id);
        }

        conventionStore.updateKeyValue('newConvention', {
            title: convention.title,
            body: convention.body,
            images: convention.images,
            stageStands: convention.stageStands,
            stageStandImages: convention.stageStandImages,
        });

        if (convention && convention.participants && convention.participants.length && participantExists(convention.participants, 'id', user.id)) {
            this.setState({ signedUp: true });
        }

        // Auto-signup after login: the user clicked "sign up" while logged out, was sent
        // to login, and is now back on this convention. Complete the signup automatically.
        const autoSignupId = util.get('conventionSignupAfterLogin');
        if (autoSignupId
            && Number(autoSignupId) === Number(convention.id)
            && user.id
            && !participantExists(convention.participants || [], 'id', user.id)) {
            util.set('conventionSignupAfterLogin', '');
            const response = await conventionStore.signUp(user);
            if (!response || response.errorCode !== 'ALREADY_SIGNED_UP') {
                appState.trackConvention('signup', convention);
            }
            await this.loadAll();
            return;
        }

        if (convention && isConventionMember(convention, user)) {
            this.setState({ isConventionAdmin: true });
        } else if (user.id && convention.user === user.id) {
            this.setState({ isConventionAdmin: true });
        }

        // Count participant stages for each participant and add this to an object with participant id as key.
        // I only need the total number of stages for each participant.

        const participantStages = {};
        if (convention && convention.stages && convention.stages.length) {
            convention.stages.forEach((stage, idx) => {
                const participants = convention[`participants${idx + 1}`];
                if (participants && participants.length) {
                    participants.forEach(participant => {
                        if (!participantStages[participant.id]) {
                            participantStages[participant.id] = 0;
                        }
                        participantStages[participant.id] += 1;
                    });
                }
            });
        }
        this.setState({
            participantStages,
        });

        // Participant payment status is tracked via participant.paymentStatus (3 = paid)
        // No need to load sales separately - the participant data is the authoritative source

        const from = new Date(convention.startDate);
        const now = new Date();

        // The classifier sets every mutually exclusive time flag EXPLICITLY (false
        // included) so this setState merge clears stale flags on a page left open
        // across the start/end boundary — otherwise isInTheFuture latches and focus
        // mode never activates for shooters who opened the page before start.
        const timeState = resolveConventionTimeState(convention, now);
        if (timeState.isOngoing) {
            clearInterval(this.countdownTimer);
            this.setState({
                ...timeState,
                editEnabled: true,
            });
        } else if (timeState.isInTheFuture) {
            this.setState(timeState);
            const { seconds } = util.dateDiff(now, from);
            this.setState({
                countdown: {
                    seconds,
                },
            });
            clearInterval(this.countdownTimer); // Clear any previous countdown
            this.countdownTimer = setInterval(() => {
                // Update the count down every 1 second
                const { seconds } = util.dateDiff(new Date(), convention.startDate);
                this.setState({
                    countdown: {
                        seconds,
                    },
                });
            }, 1000);
        } else {
            clearInterval(this.countdownTimer);
            const { days } = util.dateDiff(now, from);
            this.setState({
                ...timeState,
                age: days,
                editEnabled: days < -7 ? false : true,
            });
        }

        if (convention && convention.resultsLocked) {
            this.setState({
                editEnabled: !convention.resultsLocked,
            });
        }

        conventionStore.setReloadTimer(setTimeout(() => {
            this.loadAll();
        }, 60000));

        // Crew messages are officials-only: load them after the convention so the
        // badge + drawer refresh on every 60s fallback reload. Non-officials skip
        // the call entirely (the API would 403 it anyway).
        {
            const { userStore, conventionMessageStore, shooterMarkStore, conventionStore: cStore } = this.props.stores;
            const { user: crewUser = {}, isAdmin: crewIsAdmin, isCustomerAdmin: crewIsCustomerAdmin } = userStore;
            const crewConvention = cStore.convention || {};
            const crewIsOfficial = crewIsAdmin || crewIsCustomerAdmin
                || isConventionMember(crewConvention, crewUser)
                || isConventionOrganizer(crewConvention, crewUser)
                || (!!crewUser.id && crewConvention.user === crewUser.id);
            if (crewIsOfficial && crewConvention.id) {
                conventionMessageStore.loadForConvention(crewConvention.id);
                shooterMarkStore.loadForConvention(crewConvention.id);
            }
        }
    }

    markLiveUpdate = () => {
        this.setState({ lastLiveUpdate: Date.now() });
        // Re-arm the 60s fallback loop if a drawer dismiss (or anything) disarmed it.
        const { conventionStore } = this.props.stores;
        if (!conventionStore.reloadTimer) {
            conventionStore.setReloadTimer(setTimeout(() => this.loadAll(), 60000));
        }
    };

    setupLiveConsumer = (conventionId) => {
        if (!conventionId) {
            return;
        }
        const { conventionStore, conventionResultStore, userStore, appState } = this.props.stores;

        // Already subscribed to this convention — nothing to do.
        if (this.firebaseConsumer && this.consumerConventionId === conventionId) {
            return;
        }
        // Switching conventions — tear down the old subscription first.
        this.teardownLiveConsumer();

        try {
            this.consumerConventionId = conventionId;
            this.consumerReady = false;
            this.firebaseConsumer = new FirebaseConsumer(conventionId);
            this.setState({ liveActive: true });

            // Warm-up: Firestore replays the whole (never-deleted) backlog as `added`
            // events in the first snapshot tick. Ignore everything until this elapses.
            clearTimeout(this.consumerWarmupTimer);
            this.consumerWarmupTimer = setTimeout(() => { this.consumerReady = true; }, 1500);

            const ctx = () => ({
                payload: null,
                ownSenderId: conventionStore.broadcastSenderId,
                ready: this.consumerReady,
            });

            this.firebaseConsumer.setCommand('result', (payload) => {
                if (!shouldApplyCommand({ ...ctx(), payload, editingKeys: Object.keys(this.savingPromises) })) {
                    return;
                }
                conventionResultStore.updateResultLocal(payload);
                this.markLiveUpdate();
            });

            const handleStructural = (kind) => (payload) => {
                if (!shouldApplyCommand({ ...ctx(), payload, editingKeys: [] })) {
                    return;
                }
                this.markLiveUpdate();
                const me = userStore.user?.id;
                if (me && payload.participantId === me && typeof appState.addToast === 'function') {
                    appState.addToast({
                        type: 'info',
                        message: kind === 'added'
                            ? <Text id='convention.live-you-added'>You were added to a round</Text>
                            : <Text id='convention.live-you-removed'>You were removed from a round</Text>,
                    });
                }
                // Structural change: no payload to patch — debounce a full reload.
                clearTimeout(this.structuralReloadTimer);
                this.structuralReloadTimer = setTimeout(() => this.loadAll(), 1500);
            };
            this.firebaseConsumer.setCommand('participantAdded', handleStructural('added'));
            this.firebaseConsumer.setCommand('participantRemoved', handleStructural('removed'));

            this.firebaseConsumer.setCommand('crewMessage', (payload) => {
                if (!shouldApplyCommand({ ...ctx(), payload, editingKeys: [] })) {
                    return;
                }
                const { conventionMessageStore } = this.props.stores;
                conventionMessageStore.loadForConvention(conventionId);
                this.markLiveUpdate();
            });
        } catch (err) {
            console.error('[convention] live consumer setup failed:', err);
            this.setState({ liveActive: false });
        }
    };

    teardownLiveConsumer = () => {
        clearTimeout(this.consumerWarmupTimer);
        clearTimeout(this.structuralReloadTimer);
        if (this.firebaseConsumer) {
            try {
                this.firebaseConsumer.tearDown();
            } catch (err) {
                console.error('[convention] live consumer teardown failed:', err);
            }
            this.firebaseConsumer = null;
        }
        this.consumerConventionId = null;
        this.consumerReady = false;
    };

    postRecapToFeed = async () => {
        const { conventionStore, conventionResultStore, appState, gpsPointStore } = this.props.stores;
        const { convention, participants, classMap, disciplineMap } = conventionStore;
        const { conventionResults } = conventionResultStore;
        const { drawerHeightMediumLarge } = appState;
        const { drawerLevel = 1 } = this.props;

        // Weather rides into the recap as a "## Vær" block. Best-effort: a failed
        // or empty weather lookup must never block posting the recap.
        let weatherSummary = null;
        try {
            const weatherResponse = await gpsPointStore.getConventionWeatherSummary({ conventionId: convention.id });
            if (weatherResponse && weatherResponse.status === 200 && weatherResponse.data) {
                weatherSummary = weatherResponse.data;
            }
        } catch (err) {
            console.error('[convention recap] weather lookup failed:', err);
        }

        let draft;
        try {
            draft = buildConventionRecap({
                convention,
                conventionResults,
                participants,
                classMap,
                disciplineMap,
                conventionResultStore,
                weatherSummary,
            });
        } catch (err) {
            console.error('[convention recap] build failed:', err);
            if (typeof appState.addToast === 'function') {
                appState.addToast({
                    type: 'danger',
                    message: 'Kunne ikke generere oppsummering',
                });
            }
            return;
        }

        // Event photos flagged "Add to feed" ride into the summary draft as the
        // article's images. Strip the eventPhotos bookkeeping so the article
        // gets clean, standard image objects. The draft is still reviewed in
        // the article drawer before publishing.
        const feedPhotos = (convention.eventPhotos || [])
            .filter(p => p && p.addToFeed)
            .map(({ uploadedBy, uploadedByName, uploadedDate, addToFeed, aiIllustration, ...img }) => img);

        // AI-drawn Western-cartoon illustration of the day (weather, shooters,
        // shots fired, the course's own targets), appended as the LAST image.
        // The server also stores it as an event photo; loadRecapImage mirrors
        // that into the album on the page. Best-effort like the weather
        // lookup: a failure gives a toast and the recap still opens.
        const recapImage = await this.loadRecapImage({ convention, facts: draft.facts, weatherSummary });
        const images = recapImage ? [...feedPhotos, recapImage] : feedPhotos;

        // ArticleEdit remounts on drawer open and rebuilds newArticle from the
        // prefill prop, so everything must travel via prefill. userOnly renders
        // the simple feed composer (same as the feed's "Add new post").
        appState.openDrawer('newArticle', {
            height: drawerHeightMediumLarge,
            isNew: true,
            category: 'feed',
            active: 1,
            userOnly: 1,
            prefill: {
                title: draft.title,
                ingress: draft.ingress,
                body: draft.body,
                ...(images.length > 0 ? { images } : {}),
            },
            callback: () => {},
        }, drawerLevel + 2);
    };

    // The illustration is a background job on the server (two OpenAI image
    // calls, 60-120 s, longer than the proxy allows): POST starts it (202, or
    // 409 with the uuid of a job already running), then GET is polled until
    // the job is completed or failed, or we give up.
    pollRecapImageJob = async ({ convention, uuidv4 }) => {
        const startedAt = Date.now();
        while (Date.now() - startedAt < RECAP_IMAGE_POLL_MAX_MS) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise(resolve => setTimeout(resolve, RECAP_IMAGE_POLL_INTERVAL_MS));
            if (this.unmounted) {
                return null;
            }
            let job = null;
            try {
                // eslint-disable-next-line no-await-in-loop
                const response = await util.fetchApi('/api/ai/convention-recap-image', { method: 'GET' }, {
                    convention: convention.id,
                    uuidv4,
                });
                job = response && response.status === 200 ? response.data : null;
            } catch (err) {
                job = null;
            }
            if (job && job.status !== 'pending') {
                return job;
            }
        }
        console.error('[convention recap] illustration job timed out:', uuidv4);
        return null;
    };

    loadRecapImage = async ({ convention, facts = {}, weatherSummary }) => {
        const { appState, conventionStore } = this.props.stores;
        this.setState({ recapImageInProgress: true });
        try {
            const weather = weatherSummary ? {
                minTemp: weatherSummary.minTemp,
                maxTemp: weatherSummary.maxTemp,
                maxWind: weatherSummary.maxWind,
                totalPrecipitation: weatherSummary.totalPrecipitation,
                mostCommonSymbol: weatherSummary.mostCommonSymbol,
            } : null;
            const response = await util.fetchApi('/api/ai/convention-recap-image', { method: 'POST' }, {
                convention: convention.id,
                participantCount: facts.participantCount,
                shotsFired: facts.shotsFired,
                stagesCount: facts.stagesCount,
                weather,
            });
            const started = response && (response.status === 202 || response.status === 409)
                && response.data && response.data.uuidv4;
            const job = started ? await this.pollRecapImageJob({ convention, uuidv4: response.data.uuidv4 }) : null;
            if (job && job.status === 'completed' && job.image) {
                const { photo } = job;
                if (photo && photo.md5) {
                    const existing = Array.isArray(convention.eventPhotos) ? convention.eventPhotos : [];
                    if (!existing.some(p => p && p.md5 === photo.md5)) {
                        conventionStore.updateObjectKeyValue('convention', 'eventPhotos', [...existing, photo]);
                    }
                }
                return job.image;
            }
            if (this.unmounted) {
                return null;
            }
            console.error('[convention recap] illustration failed:', (job && job.error) || (response && response.message));
        } catch (err) {
            console.error('[convention recap] illustration failed:', err);
        } finally {
            this.setState({ recapImageInProgress: false });
        }
        if (typeof appState.addToast === 'function') {
            appState.addToast({
                type: 'warning',
                message: 'Fikk ikke laget illustrasjon. Oppsummeringen åpnes uten bilde.',
            });
        }
        return null;
    };

    editConvention = e => {
        const { id, title } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
		const { drawerHeightLarge } = appState;
		appState.openDrawer('editConvention', {
            id,
			height: drawerHeightLarge,
            callback: () => {
                this.loadAll();
            },
		}, drawerLevel + 2);
	}

    openNewNews = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const { convention } = conventionStore;
        appState.openDrawer('newsNew', {
            height: drawerHeightMediumLarge,
            isNew: true,
            conventionId: convention.id,
            callback: () => {
                this.loadNews();
            },
        }, drawerLevel + 2);
    }

    openShotTrainer = (e) => {
        e?.preventDefault?.();
        const { appState } = this.props.stores;
        if (appState.showTopSheet && appState.topSheetComponent === ShotTrainerFieldCompact) {
            appState.closeTopSheet();
            return;
        }
        // Fixed height: the compact trainer's content is fixed-size (idle rows ~128px,
        // countdown circle 130px), so a viewport-relative height only adds dead space.
        // 155px = content + the top sheet's 18px overlapping handle strip.
        appState.openTopSheet(ShotTrainerFieldCompact, { title: 'Skytetrener' }, { height: '155px' });
    }

    openAdminRemoteDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionAdminRemote', {
            height: drawerHeightLarge,
            callback: () => this.loadAll(),
        }, drawerLevel + 2);
    }

    openAdminPanelDrawer = () => {
        const { isInThePast } = this.state;
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawerRight('conventionAdminPanel', {
            height: drawerHeightLarge,
            isInThePast,
            loadAll: () => this.loadAll(),
        }, drawerLevel + 2);
    }

    openAdminOrganizersDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionAdminOrganizers', {
            height: drawerHeightLarge,
            loadAll: () => this.loadAll(),
        }, drawerLevel + 2);
    }

    openAdminKanban = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) return;
        route(`/convention/${convention.id}/admin/kanban`);
    }

    openTrainingSmsDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionAdminTrainingSms', {
            height: drawerHeightLarge,
        }, drawerLevel + 2);
    }

    openDugnadInviteDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionAdminDugnadInvite', {
            height: drawerHeightLarge,
        }, drawerLevel + 2);
    }

    openCrewMessagesDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawerRight('conventionCrewMessages', {
            height: drawerHeightLarge,
            stand: 0,
        }, drawerLevel + 2);
    }

    openAdminPaymentsDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionPaymentSummary', {
            height: drawerHeightLarge,
        }, drawerLevel + 2);
    }

    openAdminScoring = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) return;
        // IPSC (dynamisk) stevner are scored per stage on the RO page, not the NSF grid.
        if (convention.type === 'ipsc') {
            route(`/convention/${convention.id}/admin/ipsc`);
            return;
        }
        route(`/convention/${convention.id}/admin/scoring`);
    }

    openPrizesAdminDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionPrizesAdmin', {
            height: drawerHeightLarge,
        }, drawerLevel + 2);
    }

    openCourseDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionCourse', {
            height: drawerHeightLarge,
        }, drawerLevel + 2);
    }

    openAdminCopyDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionBulkGenerateConventions', {
            height: drawerHeightLarge,
            callback: () => this.loadAll(),
        }, drawerLevel + 2);
    }

    openAdminFeltDrawer = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionAdminFelt', {
            id: convention.id,
            height: drawerHeightLarge,
            callback: () => this.loadAll(),
        }, drawerLevel + 2);
    }

    addTag = e => {
        const { tagInput = '' } = this.state;
        const { conventionStore } = this.props.stores;
        const value = {
            name: tagInput.toLowerCase().trim(),
            date: new Date(),
        };
        value.md5 = md5(JSON.stringify(value)).toString();

        const field = 'tags';
        const object = conventionStore.convention;
        const tagExists = object[field] && object[field].find(tag => tag.name === value.name);
        if (!tagExists) {
            const localValue = object[field] ? [...object[field]] : [];
            conventionStore.saveField(object.id, field, value);
            conventionStore.updateField(object.id, field, [...localValue, value]);
        }
        this.setState({ tagInput: '' });
    }

    checkForEnter = (e) => {
        if (e.key === 'Enter') {
            this.addTag();
        }
    }

    isFieldShooting = (type) => {
        const isFieldShooting = ['norgesfelt', 'felt', 't-96'].includes(type);
        return isFieldShooting;
    }

    isTimeStage = (type) => {
        return ['tidsoving', 'tidskonk'].includes(type);
    }

    filterParticipants = (participant, isAdminRole, stageDone, stageClosed) => {
        const { userStore, conventionStore } = this.props.stores;
        const { user } = userStore;
        const { convention } = conventionStore;
        // Normal users only ever see their own scorecard — never anyone else's,
        // regardless of stage status or the Meg/Alle toggle.
        if (!isAdminRole) {
            return user.id === participant.id;
        }
        // Admins: everyone is visible once a stage is finished/paused or when
        // scores are entered from the Score page, otherwise honour the toggle.
        const { showAll } = this.state;
        if (stageDone || stageClosed || convention.isAdminScoring || showAll) {
            return true;
        }
        return user.id === participant.id;
    }

    toggleShowAll = () => {
        const { showAll } = this.state;
        this.setState({ showAll: !showAll });
    }

    selectConventionTab = (tab) => {
        this.setState({ conventionTab: tab });
    }

    setMemberProductCount = (count) => {
        if (this.state.memberProductCount !== count) {
            this.setState({ memberProductCount: count });
        }
    }

    toggleAdminToolbarMore = () => {
        this.setState(prevState => ({ adminToolbarMore: !prevState.adminToolbarMore }));
    }

    // The admin toolbar is a fixed overlay; its height depends on viewport width and
    // the "more" toggle, so we measure it and pad the content below accordingly.
    setAdminToolbarRef = (el) => {
        if (this.adminToolbarObserver) {
            this.adminToolbarObserver.disconnect();
            this.adminToolbarObserver = null;
        }
        this.adminToolbarEl = el;
        if (el && typeof ResizeObserver !== 'undefined') {
            this.adminToolbarObserver = new ResizeObserver(() => this.measureAdminToolbar());
            this.adminToolbarObserver.observe(el);
        }
        this.measureAdminToolbar();
    }

    measureAdminToolbar = () => {
        if (this.unmounted) return;
        const height = this.adminToolbarEl ? Math.round(this.adminToolbarEl.offsetHeight) : 0;
        if (height !== this.state.adminToolbarHeight) {
            this.setState({ adminToolbarHeight: height });
        }
    }

    toggleFocusMode = () => {
        const { isInTheFuture, focusMode } = this.state;
        const { userStore, conventionStore } = this.props.stores;
        const { user = {} } = userStore;
        const { convention } = conventionStore;
        const isSignedUpToRunningStage = !!convention.stages?.some((s, idx) => {
            const isStageRunning = !parseInt(s.stageDone, 10) && !parseInt(s.stageClosed, 10);
            if (!isStageRunning) return false;
            const stageParticipants = convention[`participants${idx + 1}`];
            return stageParticipants?.some(p => p.id === user.id);
        });
        // Must mirror the render default (focus ON for anyone — admins too —
        // signed up to a running stage, once the convention has started, even
        // past its scheduled end), or the first tap flips the wrong way.
        const currentlyActive = focusMode !== undefined
            ? focusMode
            : (!isInTheFuture && isSignedUpToRunningStage);
        this.setState({ focusMode: !currentlyActive });
    }

    updateResult = e => {
        // const { isConventionAdmin } = this.state;
        // if (!isConventionAdmin) {
        //     return;
        // }
        const { value, dataset, name, min, max } = e.target;
        const { participant, idx, id, stage, stand, delay = 500, type } = dataset;

        const isTimeStage = this.isTimeStage(type);

        // Use parseFloat for time stages, parseInt for regular scoring
        let inputValue = isTimeStage ? (parseFloat(value) || 0) : (parseInt(value, 10) || 0);

        // console.log({ min, max });
        const realMin = min ? parseFloat(min) : 0;
        const realMax = max ? parseFloat(max) : (isTimeStage ? 9999 : 50);
        if (inputValue < realMin) {
            inputValue = realMin;
        }
        if (inputValue > realMax) {
            inputValue = realMax;
        }

        // data-participant={participant.id}
        // data-id={targetResult?.id}
        // data-stage={stage.md5}
        // data-stand={stand}

        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;

        // Check for validity
        if (type === 'norgesfelt') {
            const currentStand = parseInt(stand, 10);

            const norgesFeltTargetMaxValues = conventionResultStore.getNorgesfeltTargetMaxValues();
            const currentMaxValues = norgesFeltTargetMaxValues.find(item => item.stand === currentStand);

            if (name === 'scoreHits' && inputValue > currentMaxValues.scoreHits) {
                inputValue = currentMaxValues.scoreHits;
            }
            if (name === 'scoreTargets' && inputValue > currentMaxValues.scoreTargets) {
                inputValue = currentMaxValues.scoreTargets;
            }
            if (name === 'scoreInner' && inputValue > currentMaxValues.scoreInner) {
                inputValue = currentMaxValues.scoreInner;
            }
        } else if (type === 'felt') {
            const stageObj = convention.stages?.find(s => s.md5 === stage) || { type: 'felt' };
            const { scoreHits: maxHits, scoreTargets: maxTargets, scoreInner: maxInner }
                = getStandMaxValues(stageObj, stand, convention);
            if (name === 'scoreHits' && maxHits != null && inputValue > maxHits) {
                inputValue = maxHits;
            }
            if (name === 'scoreTargets' && maxTargets != null && inputValue > maxTargets) {
                inputValue = maxTargets;
            }
            if (name === 'scoreInner' && maxInner != null && inputValue > maxInner) {
                inputValue = maxInner;
            }
        } else if (type === 't-96') {
            const { scoreHits: maxHits, scoreTargets: maxTargets, scoreInner: maxInner }
                = conventionResultStore.getT96MaxPerSeries();
            if (name === 'scoreHits' && inputValue > maxHits) {
                inputValue = maxHits;
            }
            if (name === 'scoreTargets' && inputValue > maxTargets) {
                inputValue = maxTargets;
            }
            if (name === 'scoreInner' && inputValue > maxInner) {
                inputValue = maxInner;
            }
        }

        // Validate ID - dataset values are strings, need to handle "undefined", "null", empty string, NaN
        const parsedId = id && id !== 'undefined' && id !== 'null' && id !== '' ? parseInt(id, 10) : undefined;
        const validId = parsedId && !isNaN(parsedId) ? parsedId : undefined;

        // Optimistic update - update local store IMMEDIATELY so input keeps its value
        const localUpdateData = {
            id: validId,
            targetIndex: parseInt(idx, 10),
            participant: parseInt(participant, 10),
            convention: convention.id,
            stage,
            [name]: inputValue,
        };
        if (stand) {
            localUpdateData.stand = parseInt(stand, 10);
        }
        conventionResultStore.updateResultLocal(localUpdateData);

        // Score celebrations for regular shooting (not field shooting or time stages)
        if (name === 'scoreTotal' && type !== 'norgesfelt' && type !== 'felt' && !isTimeStage) {
            if (inputValue === 50) {
                triggerPerfectScoreConfetti();
                triggerGreenFlash(e.target);
            } else if (inputValue === 49) {
                triggerAlmostPerfectSound();
                triggerScreenShake();
                triggerOrangeFlash(e.target);
            } else if (inputValue >= 45) {
                triggerApplause();
                triggerGreenFlash(e.target);
            }
        }

        const delayValue = parseInt(delay, 10) || 500;
        // console.log('updateResult', participant, id, idx, value);
        clearTimeout(this.saveTimer[`${participant}-${stage}-${stand}-${name}`]);
        const timerId = `${participant}-${stage}-${stand}-${name}`;
        clearTimeout(this.updateTimer[timerId]);
        this.updateTimer[timerId] = setTimeout(() => {
            this.doUpdateResult(e, inputValue);
        }, parseInt(delayValue, 10));

        // Field-shooting cells (norgesfelt/felt/t-96) are single-digit, so move focus to
        // the next cell IMMEDIATELY, here in the onInput gesture. The previous auto-advance
        // lived at the end of the debounced+awaited doUpdateResult, which fired hundreds of
        // ms late; on iOS a programmatic focus() outside a user gesture is ignored outright,
        // so fast entry piled multiple digits into one cell (clamped to max) and left the
        // rest blank (6-6-6 → 6-0-0), and on desktop the late focus yanked the cursor
        // backward onto an already-filled, re-selected cell (6-6-3 → 6-2-0).
        if (this.isFieldShooting(type) && String(value).length > 0) {
            this.advanceScoreFocus({ participant, stage, stand, name });
        }
    }

    advanceScoreFocus = ({ participant, stage, stand, name }) => {
        let nextStand = parseInt(stand, 10);
        let nextField;
        if (name === 'scoreHits') {
            nextField = 'scoreTargets';
        } else if (name === 'scoreTargets') {
            nextField = 'scoreInner';
        } else if (name === 'scoreInner') {
            nextField = 'scoreHits';
            nextStand += 1;
        }
        if (!nextField) {
            return;
        }
        const nextEl = this.refs[`${participant}-${stage}-${nextStand}-${nextField}`];
        if (nextEl) {
            nextEl.focus();
        }
    }

    doUpdateResult = async (e, value) => {
        const { dataset, name = 'scoreTotal' } = e.target;
        const { participant, idx, id, stage, stand, type } = dataset;
        // console.log('doUpdateResult', participant, id, idx, value);
        const { appState, conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;
        clearTimeout(this.saveTimer[`${participant}-${stage}-${stand}-${name}`]);

        const isTimeStage = this.isTimeStage(type);

        // Use parseFloat for time stages, parseInt for regular scoring
        const parsedValue = isTimeStage ? parseFloat(value) : parseInt(value, 10);

        // Build the result data for server save (exclude undefined values for Firebase compatibility)
        const resultData = {
            targetIndex: parseInt(idx, 10),
            participant: parseInt(participant, 10),
            convention: convention.id,
            stage,
            [name]: parsedValue,
        };
        // Only include stand if it has a value (field shooting uses stand, time stages use targetIndex)
        if (stand) {
            resultData.stand = parseInt(stand, 10);
        }

        // Unique key for this result (use stand for field shooting, idx for other types)
        const saveKey = `${participant}-${stage}-${stand || idx}`;

        // Create a deferred promise for serialization
        let resolvePromise;
        const newPromise = new Promise(resolve => {
            resolvePromise = resolve;
        });

        // Capture previous promise before setting ours (must be synchronous)
        const previousPromise = this.savingPromises[saveKey];
        this.savingPromises[saveKey] = newPromise;

        // Wait for previous save to complete
        if (previousPromise) {
            await previousPromise;
        }

        try {
            // Get the ID - check local cache first, then from dataset, then from store
            let recordId = this.localResultIds[saveKey];
            if (!recordId) {
                // Validate ID from dataset - values are strings, handle "undefined", "null", empty string, NaN
                const parsedId = id && id !== 'undefined' && id !== 'null' && id !== '' ? parseInt(id, 10) : undefined;
                recordId = parsedId && !isNaN(parsedId) ? parsedId : undefined;
            }
            if (!recordId) {
                const { conventionResults } = conventionResultStore;
                const existingResult = conventionResults.find(r =>
                    r.participant === parseInt(participant, 10) &&
                    r.stage === stage &&
                    (stand ? r.stand === parseInt(stand, 10) : r.targetIndex === parseInt(idx, 10))
                );
                if (existingResult?.id) {
                    recordId = existingResult.id;
                    this.localResultIds[saveKey] = recordId;
                }
            }

            // Note: Optimistic update already done in updateResult()

            // Low score sound - only after debounce so it doesn't fire while typing (skip for time stages)
            if (!isTimeStage) {
                const scoreValue = parseInt(value, 10);
                if (name === 'scoreTotal' && type !== 'norgesfelt' && type !== 'felt' && scoreValue <= 20 && scoreValue > 0) {
                    triggerLowScoreSound();
                }
            }

            if (recordId) {
                conventionStore.broadcast('result', {
                    id: recordId,
                    ...resultData,
                });
                await conventionResultStore.save({
                    id: recordId,
                    ...resultData,
                });
                // Track result entered (for updates)
                appState.trackConvention('result_entered', convention, {
                    participantId: resultData.participant,
                    stage: resultData.stage,
                });
            } else {
                conventionStore.broadcast('result', resultData);
                const response = await conventionResultStore.insert(resultData);
                // Note: API returns lowercase 'conventionresult', not camelCase
                const responseResult = response?.data?.conventionresult;
                if (responseResult?.id) {
                    const newId = responseResult.id;
                    this.localResultIds[saveKey] = newId;

                    conventionResultStore.updateResultId({
                        participant: resultData.participant,
                        convention: resultData.convention,
                        stage: resultData.stage,
                        stand: resultData.stand,
                        targetIndex: resultData.targetIndex,
                        newId,
                    });
                }
                // Track result entered (for new results)
                appState.trackConvention('result_entered', convention, {
                    participantId: resultData.participant,
                    stage: resultData.stage,
                });
            }
        } finally {
            resolvePromise();
            if (this.savingPromises[saveKey] === newPromise) {
                delete this.savingPromises[saveKey];
            }
        }

        // Only reload results periodically (every 30 seconds) to sync with server
        // This prevents the UI from being emptied and refilled on every keystroke
        this.saveTimer[`${participant}-${stage}-${stand}-${name}`] = setTimeout(() => {
            // Only reload if no recent saves are pending
            const now = Date.now();
            if (!this.lastResultsLoad || (now - this.lastResultsLoad) > 30000) {
                this.lastResultsLoad = now;
                this.loadResults();
            }
        }, 5000);

        // Focus auto-advance is handled synchronously in updateResult (the onInput gesture);
        // doing it here — after the debounce + save round-trip — fired too late and was
        // ignored on iOS, scrambling fast score entry.
    }

    loadResults = async () => {
        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;
        await conventionResultStore.load({ query: { convention: convention.id }, limit: 50000 });
    }

    loadNews = async () => {
        const { conventionStore, newsStore, userStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention || !convention.id) {
            return;
        }
        const { user = {} } = userStore;
        const { isAdmin, isRaceAdmin, isMediaWorker } = user;
        const isWorkingWithMedia = isAdmin || isRaceAdmin || isMediaWorker;
        await newsStore.load({
            query: { convention: convention.id, addData: 'races' },
            url: isWorkingWithMedia ? null : '/api/news/public/',
            limit: 3,
        });
    }

    addImage = e => {
        const { id } = e.target.dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightSmall } = appState;
        appState.openDrawer('editConventionResultImage', {
            id,
            height: drawerHeightSmall,
        }, drawerLevel + 1);
    }

    // Megalink icon on a result row: the shooter's shots on that Lag, series by
    // series (components/convention/shooterShots.js). Stops the click so the
    // name cell's participant editor does not open as well.
    openShooterShots = e => {
        e.preventDefault();
        e.stopPropagation();
        const {
            id, stage, participant, name,
        } = e.currentTarget.dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('shooterShots', {
            conventionId: parseInt(id, 10),
            stageMd5: stage,
            participantId: parseInt(participant, 10),
            name,
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    renderShooterShotsIcon(convention, stage, participant) {
        return (
            <button
                type='button'
                class='btn btn-link p-0 me-1 align-baseline'
                style='font-size: 0.8rem; line-height: 1;'
                title='Vis skuddene fra Megalink'
                data-id={convention.id}
                data-stage={stage.md5}
                data-participant={participant.id}
                data-name={localUtil.displayName(participant)}
                onClick={this.openShooterShots}
            >
                <i class='fa-duotone fa-bullseye' />
            </button>
        );
    }

    viewImage = e => {
        const { id } = e.target.closest('td').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('viewConventionResultImage', {
            id,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    viewEditImage = e => {
        const { id } = e.target.closest('i').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('viewConventionResultImage', {
            id,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    handleFocus = (e) => {
        e.target.select();
    }

    // handleBlur = async (e) => {
    //     await this.loadResults();
    // }
    // handleContextMenu = (e) => {
    //     return ;
    //     e.preventDefault();
    // }

    editParticipant = async e => {
        const { id, md5, stage, idx } = e.target.closest('td, button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightSmall } = appState;
        appState.openDrawer('editConventionParticipant', {
            id,
            md5,
            stage,
            idx,
            height: drawerHeightSmall,
        }, drawerLevel + 1);
    }

    addParticipant = async e => {
        const { id, md5 } = e.target.closest('button').dataset;
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { participants } = convention;
        const user = participants.find(user => user.id === parseInt(id, 10));
        // Admin adding participant - exempt from 2-hour rule
        await conventionStore.newParticipant(user, md5, true);
        await this.loadAll();
    }

    addMe = async e => {
        const { md5 } = e.target.closest('button').dataset;
        const { conventionStore, userStore } = this.props.stores;
        const { user } = userStore;
        // User adding themselves - 2-hour rule applies
        await conventionStore.newParticipant(user, md5, false);
        await this.loadAll();
    }

    editStage = async e => {
        const { id, md5, idx } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('editConventionStage', {
            id,
            idx,
            md5,
            height: drawerHeightMedium,
        }, drawerLevel + 1);
    }

    toggleStageField = async e => {
        const { md5, field, value } = e.target.closest('button').dataset;
        const { conventionStore } = this.props.stores;
        await conventionStore.editArrayObject({
            field: 'stages',
            key: field,
            value,
            id: conventionStore.convention.id,
            md5,
        });
    }

    // «Ny Hurtig» below the last Lag once it is Ferdig: one more Lag of the same
    // type, numbered and dated by the store (previous Lag + stageInterval, Venter).
    newRoundLikeLast = async e => {
        const { type } = e.target.closest('button').dataset;
        const { conventionStore, appState } = this.props.stores;
        if (!type || this.state.creatingRound) return;
        this.setState({ creatingRound: true });
        const response = await conventionStore.newRound(type);
        if (!response || response.status >= 400 || response.errorCode) {
            appState.addToast({ type: 'error', message: (response && response.message) || 'Kunne ikke opprette nytt lag' }, 5000);
        }
        await this.loadAll();
        this.setState({ creatingRound: false });
    }

    addSignupToCart = async (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        const { userStore, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.signupProduct) {
            return;
        }
        const userId = userStore.user?.id;

        // Competitions charge the signup fee per start (per Lag); trainings charge
        // it once (requiredStarts). Count every stage the user is signed up for,
        // then charge for the starts not yet paid.
        let lagCount = 0;
        (convention.stages || []).forEach((stage, idx) => {
            const stageParticipants = convention[`participants${idx + 1}`] || [];
            if (stageParticipants.find(p => p.id === userId)) {
                lagCount += 1;
            }
        });
        const currentParticipant = (convention.participants || []).find(p => p.id === userId);
        const paidStartsCount = currentParticipant?.paidStartsCount || 0;
        const required = requiredStarts(convention, lagCount);
        // When all required starts are already paid (competition "new signup"),
        // fall back to adding a single extra start.
        const addingNewSignup = paidStartsCount >= required;
        const unpaidStarts = Math.max(1, required - paidStartsCount);

        const cart = userStore.user?.cart || [];
        const existing = cart.find(item => item.product === convention.signupProduct);
        if (existing) {
            await userStore.editCart({
                uuidv4: existing.uuidv4,
                field: 'qty',
                value: addingNewSignup ? (existing.qty || 1) + 1 : unpaidStarts,
            });
        } else {
            await userStore.addToCart({
                product: convention.signupProduct,
                qty: addingNewSignup ? 1 : unpaidStarts,
                conventionId: convention.id,
            });
        }
        route('/cart');
    }

    updateFooterFlag = () => {
        const { appState, userStore, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { signedUp, isInTheFuture, isRegistrationOpen } = this.state;
        const userId = userStore?.user?.id;
        const payState = getConventionPayState({
            convention,
            userId,
            signedUp,
            isInTheFuture,
            isRegistrationOpen,
            cartCount: countCartItems(userStore?.user?.cart),
        });
        appState.updateKeyValue('hideGlobalFooter', payState.visible);
    }

    goToCart = (e) => {
        if (e?.stopPropagation) e.stopPropagation();
        route('/cart');
    }

    componentDidMount() {
        this.loadAll(this.props, true);
        this.resetSummary();
        this.updateFooterFlag();
        // Refocusing the tab/app re-loads (and loadAll re-arms the fallback loop),
        // covering a drawer that was dismissed without firing its reload callback.
        this.handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                this.loadAll();
            }
        };
        document.addEventListener('visibilitychange', this.handleVisibility);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid || nextProps.id !== this.props.id) {
            this.loadAll(nextProps, true);
            this.resetSummary();
        }
    }

    componentDidUpdate() {
        this.updateFooterFlag();
    }

    componentWillUnmount() {
        this.unmounted = true;
        if (this.adminToolbarObserver) {
            this.adminToolbarObserver.disconnect();
            this.adminToolbarObserver = null;
        }
        const { appState } = this.props.stores;
        appState.updateKeyValue('hideGlobalFooter', false);
        clearInterval(this.countdownTimer);
        const { conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        this.teardownLiveConsumer();
        this.stopViewingSession();
        if (this.handleVisibility) {
            document.removeEventListener('visibilitychange', this.handleVisibility);
            this.handleVisibility = null;
        }
    }

    render() {
        const {
            searchTitle,
            otpTitle,
            id,
        } = this.props;
        const {
            search,
            userOtp,
            userOtpIsValid,
            userOtpError,
            participantStages = {},
            liveActive,
            lastLiveUpdate,
            recapImageInProgress,
            // participantPurchases removed - payment status is from participant.paymentStatus
        } = this.state;

        const {
            signedUp,
            isConventionAdmin: isConventionAdminState,
            isValidDate,
            isOngoing,
            isInTheFuture,
            isInThePast,
            isRegistrationOpen,
            isStarted,
            registrationFrom,
            countdown = {},
            age,
            editEnabled,
            showAll,
            focusMode,
            conventionTab = 'stevne',
            memberProductCount = 0,
            notFound,
        } = this.state;
        const { appState, userStore, conventionStore, conventionResultStore, newsStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary, language, searchUsersResult } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // A non-existent or inaccessible convention (e.g. a deleted id, or one whose
        // hostname scope doesn't match the current domain) loads as `undefined` via
        // updateItem(response.data[0]) when the API returns an empty result set. The
        // store observable then holds `undefined` instead of the initial `{}`. Coerce
        // back to an empty object so the whole render body — starting with the
        // `stageStands` destructure below — never reads a property off undefined.
        const { convention: loadedConvention, jwtToken, otp = '' } = conventionStore;
        const convention = loadedConvention || {};

        // The load came back empty: show a dead end the user can act on instead of
        // an empty convention shell. Requiring `!convention.id` too means a stale
        // flag can never hide a convention that is actually loaded.
        if (notFound && !convention.id) {
            return (<ConventionNotFound stores={this.props.stores} />);
        }

        const { stageStands = [] } = convention;

        // Reactive member/admin check. The loadAll-time state can be computed
        // before the user info has arrived (and is then never re-evaluated),
        // which locked convention members out of the admin/Score pills. Merge
        // the state with a live check so it corrects itself once both the user
        // and the convention are in place. Every member on the convention's
        // "Medlemmer" list gets scoring access — matched by id, cellphone or
        // email via isConventionMember.
        const isConventionAdmin = isConventionAdminState
            || isConventionMember(convention, user)
            || (!!user.id && convention.user === user.id);
        // Organizers (Arrangører list) are scoring crew: they get score entry
        // and crew messages (mirroring isConventionOfficial in
        // backend/lib/convention-access.js), but not the full admin pill row.
        const isOrganizerCrew = isConventionOrganizer(convention, user);
        // Officials-only shooter marks (virrete/farlig/vanskelig). Mirrors the
        // crew-messages gate; the API enforces the same rule server-side.
        const isMarkOfficial = isAdmin || isCustomerAdmin || isConventionAdmin || isOrganizerCrew;
        const norgesFeltTargetMaxValues = conventionResultStore.getNorgesfeltTargetMaxValues();

        const { conventionResults } = conventionResultStore;

        const { newsList = [] } = newsStore;
        const hasConventionNews = newsList.some(n => Number(n.convention) === Number(convention.id));

        const apiServer = util.getApiServer();

        const otpFirstPart = otp.slice(0, 3);
        const otpSecondPart = otp.slice(3, 6);

        // Organizer-crew users get the pill bar too, but only the Meld. + Score
        // pills inside it — the rest stay full-admin (isAdminRole) only.
        const showAdminPills = isConventionAdmin || isAdmin || isCustomerAdmin || isOrganizerCrew;
        const isAdminRole = isConventionAdmin || isAdmin || isCustomerAdmin;
        // A stage that is neither finished nor paused counts as active regardless
        // of the convention's scheduled end time — the last Lag often finishes
        // after the clock runs out, and focus mode must keep working until the
        // stage is actually marked Ferdig.
        const hasActiveStage = !!convention.stages?.some(s =>
            !parseInt(s.stageDone, 10) && !parseInt(s.stageClosed, 10));
        // First Lag still in Venter while nothing is Pågår: the one the officer must
        // start before the shooting begins (Megalink assigns nothing to a Lag in Venter).
        // Hidden once the stevne is over (isInThePast): a Lag nobody started then
        // needs no nagging.
        const firstWaitingStage = (hasActiveStage || isInThePast) ? null : (convention.stages || []).find(s =>
            parseInt(s.stageClosed, 10) === 1 && parseInt(s.stageDone, 10) !== 1) || null;
        const lastStage = convention.stages?.length ? convention.stages[convention.stages.length - 1] : null;
        // «Ny <type>» for admins before the stevne starts and while it runs, whatever
        // state the Lag are in, so the club can plan the Lag ahead and shooters see
        // what is coming. Never on a finished stevne that someone opens again later.
        const canAddRound = !isInThePast;
        const isSignedUpToRunningStage = !!convention.stages?.some((s, idx) => {
            const isStageRunning = !parseInt(s.stageDone, 10) && !parseInt(s.stageClosed, 10);
            if (!isStageRunning) return false;
            const stageParticipants = convention[`participants${idx + 1}`];
            return stageParticipants?.some(p => p.id === user.id);
        });
        const focusModeAvailable = !isInTheFuture && hasActiveStage && (!isAdminRole || signedUp);
        // Focus mode defaults ON for anyone signed up to a running stage —
        // admins included. A shooter on the line wants the minimal score view;
        // admins exit with one tap on the "Show all" button.
        const focusModeActive = focusModeAvailable
            && (focusMode !== undefined ? focusMode : isSignedUpToRunningStage);
        // Content starts at 80px (#app padding); the toolbar overlay starts at 60px,
        // so content must be pushed down by toolbar height - 20px plus a 12px gap.
        const { adminToolbarHeight } = this.state;
        let pillBarPadding = 20;
        if (showAdminPills && !focusModeActive) {
            pillBarPadding = adminToolbarHeight > 0
                ? adminToolbarHeight - 8
                : (isAdminRole ? 72 : 40);
        }

        // Tab navigation — split the personal section ("Mitt stevne") and the member
        // products ("Produkter") out behind tabs, but only when there is content for them.
        // When no secondary tab has content (not signed up) the page renders inline as before.
        // In focus mode the tab BAR is hidden and the selection is forced back to 'stevne'
        // (the score grid), so the page can never be stuck on a hidden tab. "Mitt stevne"
        // must still NOT show inline above the grid — it lives behind its tab.
        const isOnAnyStage = !!convention.stages?.some((s, idx) =>
            (convention[`participants${idx + 1}`] || []).some(p => p.id === user.id));
        const hasMyConventionContent = signedUp
            && (convention.signupProduct > 0 || isOnAnyStage);
        const hasProductsContent = signedUp && !!convention.customer && memberProductCount > 0;
        const tabsEngaged = hasMyConventionContent || hasProductsContent;
        // Guard against a stale selection pointing at a tab that lost its content.
        const activeTab = (tabsEngaged
            && !focusModeActive
            && ((conventionTab === 'mine' && hasMyConventionContent)
                || (conventionTab === 'produkter' && hasProductsContent)))
            ? conventionTab
            : 'stevne';
        const showStevneTab = !tabsEngaged || activeTab === 'stevne';
        const showMyConvention = tabsEngaged ? activeTab === 'mine' : signedUp;
        const showProductsTab = !tabsEngaged || activeTab === 'produkter';
        const currentParticipant = (convention.participants || []).find(p => p.id === user.id);
        const signupUnpaid = convention.signupProduct > 0
            && !!currentParticipant
            && currentParticipant.paymentStatus !== 3;

        const { conventionMessageStore } = this.props.stores;
        const { adminToolbarMore } = this.state;
        // Non-admin crew only see Meld./Score, so nothing is tucked behind the toggle for them.
        const showMoreAdminTools = !isAdminRole || adminToolbarMore;
        const crewUnread = unreadCount(conventionMessageStore.conventionMessages, {
            lastSeenTs: getLastSeen(convention.id, 0),
            stand: 0,
            userId: user.id,
        });

        return (<>
            {showAdminPills && !focusModeActive && (
                <div
                    class='position-fixed d-flex flex-column align-items-center px-2'
                    style='
                        z-index: 1050;
                        top: calc(60px + var(--native-safe-area-top, 0px));
                        left: 0;
                        right: 0;
                        font-size: 1rem;
                        line-height: 1.2rem;
                        pointer-events: none;
                    '
                >
                    <div
                        ref={this.setAdminToolbarRef}
                        class='d-flex flex-row flex-wrap justify-content-center align-items-center gap-1'
                        style='max-width: 100%; pointer-events: auto;'
                    >
                        {isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openAdminPanelDrawer}
                        >
                            <i class='fa-duotone fa-screwdriver-wrench me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-panel-short'>Admin</Text></small>
                        </button>}
                        {isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openAdminKanban}
                        >
                            <i class='fa-duotone fa-grip-dots-vertical me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-kanban-short'>Lag</Text></small>
                        </button>}
                        {convention.stages?.length > 0 && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openAdminScoring}
                        >
                            <i class='fa-regular fa-bullseye-arrow me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-scoring-short'>Score</Text></small>
                        </button>}
                        {isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openAdminRemoteDrawer}
                        >
                            <i class='fa-duotone fa-tower-broadcast me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-remote-short'>Fjern</Text></small>
                        </button>}
                        {isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openShotTrainer}
                        >
                            <i class='fa-duotone fa-stopwatch me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-shottrainer-short'>Trainer</Text></small>
                        </button>}
                        {isAdminRole && convention.type === 'dugnad' && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openDugnadInviteDrawer}
                        >
                            <i class='fa-duotone fa-paper-plane me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-dugnad-invite-short'>Invite</Text></small>
                        </button>}
                        {showMoreAdminTools && isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openAdminOrganizersDrawer}
                        >
                            <i class='fa-duotone fa-users-gear me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-organizers-short'>Arr.</Text></small>
                        </button>}
                        {showMoreAdminTools && isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openAdminPaymentsDrawer}
                        >
                            <i class='fa-duotone fa-cash-register me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-payments-short'>Bet.</Text></small>
                        </button>}
                        {showMoreAdminTools && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openCrewMessagesDrawer}
                        >
                            <i class='fa-duotone fa-messages me-1' style='font-size: 1.2rem;' />
                            <small><Text id='conventionCrewMessages.admin-short'>Meld.</Text></small>
                            {crewUnread > 0 && (
                                <span class='badge rounded-pill bg-warning text-dark ms-1'>{crewUnread}</span>
                            )}
                        </button>}
                        {showMoreAdminTools && isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openPrizesAdminDrawer}
                        >
                            <i class='fa-duotone fa-gift me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-prizes-short'>Prem.</Text></small>
                        </button>}
                        {showMoreAdminTools && isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.openTrainingSmsDrawer}
                        >
                            <i class='fa-duotone fa-message-sms me-1' style='font-size: 1.2rem;' />
                            <small><Text id='convention.admin-training-sms-short'>SMS</Text></small>
                        </button>}
                        {isAdminRole && <button
                            type='button'
                            class='btn btn-danger rounded-pill d-flex flex-row align-items-center px-3 py-1'
                            style='min-height: 44px;'
                            onClick={this.toggleAdminToolbarMore}
                            aria-expanded={adminToolbarMore}
                            title={adminToolbarMore ? 'Vis færre' : 'Vis flere'}
                        >
                            <i class={`fa-solid ${adminToolbarMore ? 'fa-xmark' : 'fa-ellipsis'}`} style='font-size: 1.2rem;' />
                            {!adminToolbarMore && crewUnread > 0 && (
                                <span class='badge rounded-pill bg-warning text-dark ms-1'>{crewUnread}</span>
                            )}
                        </button>}
                    </div>
                </div>
            )}
            <ConventionPayBottomBar
                stores={this.props.stores}
                convention={convention}
                signedUp={signedUp}
                isInTheFuture={isInTheFuture}
                isRegistrationOpen={isRegistrationOpen}
                onPay={this.addSignupToCart}
                onGoToCart={this.goToCart}
            />
            <div class='w-100 d-flex justify-content-center'>
                {/* The fixed admin pill bar (one row for crew, two for admins) sits
                    where the title would be — make room for it instead of overlapping. */}
                <div class='w-100 h-100' style={`max-width: 960px; padding-top: ${pillBarPadding}px; padding-bottom: 100px; transition: padding-top 0.15s ease-out;`}>

                    {!!convention.isCancelled && (
                        <div class='alert alert-danger d-flex flex-row align-items-center mx-3 mb-3'>
                            <i class='fa-solid fa-ban me-3' style='font-size: 1.8rem;' />
                            <div>
                                <div class='fw-bold text-uppercase'>
                                    <Text id='convention.cancelled'>Cancelled</Text>
                                </div>
                                <div>
                                    <Text id='convention.cancelled-info'>This convention has been cancelled.</Text>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Focus mode: shooters (and participating admins) get only the
                        score grid + the Show all button — no title, no tabs. */}
                    {!focusModeActive && (
                    <div class={`font-weight-lighter px-3 mt-0 box-header w-100 position-relative`}>
                        {convention.title}
                        <LiveIndicator active={liveActive} lastUpdate={lastLiveUpdate} />
                        <div class={`position-absolute d-flex gap-1`} style={`top: 0px; right: 10px; z-index: 1000;`}>
                            {isCustomerAdmin && (
                                <button
                                    onClick={this.editConvention}
                                    class={`btn btn-sm btn-outline-primary rounded-pill`}
                                    data-id={convention.id}
                                >
                                    <i class={`fa-regular fa-pen`} />
                                </button>
                            )}
                            {(isConventionAdmin || isAdmin || isCustomerAdmin) && (
                                <Localizer>
                                    <button
                                        type='button'
                                        onClick={this.openNewNews}
                                        class={`btn btn-sm btn-outline-primary rounded-pill`}
                                        title={<Text id='convention.add-news'>Add news</Text>}
                                    >
                                        <i class={`fa-regular fa-plus`} />
                                    </button>
                                </Localizer>
                            )}
                            {(isConventionAdmin || isAdmin || isCustomerAdmin)
                                && isInThePast
                                && convention.participants?.length > 0
                                && convention.stages?.length > 0 && (
                                <Localizer>
                                    <button
                                        onClick={this.postRecapToFeed}
                                        disabled={recapImageInProgress}
                                        class={`btn btn-sm btn-outline-info rounded-pill`}
                                        title={recapImageInProgress
                                            ? <Text id='convention.drawing-illustration'>Drawing illustration...</Text>
                                            : <Text id='convention.post-recap-to-feed'>Post recap to feed</Text>}
                                    >
                                        {recapImageInProgress ? (
                                            <>
                                                <i class='fa-solid fa-spinner fa-spin' />
                                                {' '}
                                                <Text id='convention.drawing-illustration'>Drawing illustration...</Text>
                                            </>
                                        ) : (
                                            <i class={`fa-regular fa-newspaper`} />
                                        )}
                                    </button>
                                </Localizer>
                            )}
                        </div>
                    </div>
                    )}
                    <div class='w-100 d-flex flex-column'>
                        <div class='px-0 box-header-info d-flex flex-column'>

                            {tabsEngaged && !focusModeActive && (
                                <ul class='nav nav-pills justify-content-center mb-2 px-3'>
                                    <li class='nav-item'>
                                        <button
                                            type='button'
                                            class={`nav-link ${activeTab === 'stevne' ? 'active' : ''}`}
                                            onClick={() => this.selectConventionTab('stevne')}
                                        >
                                            <Text id='convention.tab-stevne'>Event</Text>
                                        </button>
                                    </li>
                                    {hasMyConventionContent && (
                                        <li class='nav-item'>
                                            <button
                                                type='button'
                                                class={`nav-link ${activeTab === 'mine' ? 'active' : ''}`}
                                                onClick={() => this.selectConventionTab('mine')}
                                            >
                                                <Text id='convention.tab-mine'>My event</Text>
                                                {signupUnpaid && (
                                                    <span class='badge text-bg-warning ms-2'>
                                                        <i class='fa-solid fa-coins' />
                                                    </span>
                                                )}
                                            </button>
                                        </li>
                                    )}
                                    {hasProductsContent && (
                                        <li class='nav-item'>
                                            <button
                                                type='button'
                                                class={`nav-link ${activeTab === 'produkter' ? 'active' : ''}`}
                                                onClick={() => this.selectConventionTab('produkter')}
                                            >
                                                <Text id='convention.tab-products'>Products</Text>
                                                <span class='badge bg-secondary ms-2'>{memberProductCount}</span>
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            )}

                            {showStevneTab && (<>

                            {!focusModeActive && (
                                <ConventionViewInfo
                                    stores={this.props.stores}
                                    isStarted={isStarted}
                                    isOngoing={isOngoing}
                                    isInThePast={isInThePast}
                                    signedUp={signedUp}
                                    drawerLevel={this.props.drawerLevel}
                                />
                            )}

                            {!focusModeActive && (
                                <ConventionViewSignup
                                    stores={this.props.stores}
                                    isOngoing={isOngoing}
                                    isInTheFuture={isInTheFuture}
                                    isInThePast={isInThePast}
                                    isRegistrationOpen={isRegistrationOpen}
                                    signedUp={signedUp}
                                    isConventionAdmin={isConventionAdmin}
                                    countdown={countdown}
                                    loadAll={this.loadAll}
                                />
                            )}

                            {/* Description card: before and after the stevne only, like weather
                                and the signup list — while it runs the Lag blocks carry the day. */}
                            {!focusModeActive && !isOngoing && (
                                <ConventionViewInfo
                                    stores={this.props.stores}
                                    part='description'
                                    isStarted={isStarted}
                                    isOngoing={isOngoing}
                                    isInThePast={isInThePast}
                                    signedUp={signedUp}
                                    drawerLevel={this.props.drawerLevel}
                                />
                            )}

                            {!focusModeActive && (
                                <ConventionParticipantsDone
                                    stores={this.props.stores}
                                    isInThePast={isInThePast}
                                />
                            )}

                            {!focusModeActive && convention.id ? (
                                <div class='px-3 mt-3 mb-1'>
                                    <a
                                        href={`/convention/${convention.id}/results`}
                                        class='btn btn-lg btn-primary rounded-pill w-100'
                                    >
                                        <i class='fa-duotone fa-list-ol me-2' />
                                        <Text id='convention.live-results-pill'>Results</Text>
                                    </a>
                                </div>
                            ) : null}

                            {!focusModeActive && convention.id ? (
                                <ConventionMyNextStage
                                    stores={this.props.stores}
                                    convention={convention}
                                />
                            ) : null}

                            {/* Weather and the signup list matter before and after the stevne,
                                not while it is running — the Lag blocks carry the day. */}
                            {!focusModeActive && !isOngoing && convention.id && convention.lat && convention.lng ? (
                                <ConventionWeatherSummary
                                    conventionId={convention.id}
                                    stores={this.props.stores}
                                />
                            ) : null}

                            {!focusModeActive && (
                                <ConventionViewSponsors
                                    stores={this.props.stores}
                                />
                            )}

                            {!focusModeActive && hasConventionNews && (
                                <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                                    <div class='w-100 d-flex flex-column'>
                                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                            <Text id='convention.news'>News</Text>
                                        </div>
                                        <div class={`d-flex flex-column mx-3 px-0 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                            <NewsList
                                                key={`newsList-convention-${convention.id}`}
                                                stores={this.props.stores}
                                                conventionId={convention.id}
                                                largeList={false}
                                                showHeader={false}
                                                hideAdminButtons={true}
                                                limit={3}
                                            />
                                            <div class='d-flex flex-row justify-content-center border-top mt-2 pt-2'>
                                                <a href={`/convention/${convention.id}/news`}>
                                                    <Text id='convention.see-all-news'>See all news</Text> &raquo;
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            </>)}

                            {showMyConvention && convention.id && (<>
                                <ConventionMyPurchases
                                    stores={this.props.stores}
                                    convention={convention}
                                />
                                <ConventionMyAnalysis
                                    stores={this.props.stores}
                                    convention={convention}
                                />
                            </>)}

                            {/* Products live on their own tab. Keep mounted (hidden via d-none
                                when off-tab) so the component can report its count back, which
                                drives whether the Produkter tab is offered at all. Mounted in
                                focus mode too (hidden) so the Produkter tab keeps working. */}
                            {signedUp && convention.customer ? (
                                <div class={showProductsTab ? '' : 'd-none'}>
                                    <MemberProducts
                                        stores={this.props.stores}
                                        customer={convention.customer}
                                        convention={convention}
                                        onCount={this.setMemberProductCount}
                                        startExpanded={true}
                                    />
                                </div>
                            ) : null}

                            {showStevneTab && (<>

                            {(isInThePast && convention.id && conventionResults.length > 0) ? null : <>
                                {!convention.hideParticipants && !focusModeActive && !isOngoing ? <>
                                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-3'>
                                        <Text id='convention.participants'>Participants</Text>
                                    </div>
                                    <div class={`d-flex flex-wrap gap-1 mt-0 mb-3 mx-3 px-3 py-2 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto`}>
                                        {convention.participants && [...convention.participants].sort((a, b) => {
                                            // Sort by stage count ascending (fewer stages first), then by signup date ascending (signed up first)
                                            const stagesA = participantStages[a.id] || 0;
                                            const stagesB = participantStages[b.id] || 0;
                                            if (stagesA !== stagesB) return stagesA - stagesB;
                                            const dateA = a.signupDate ? new Date(a.signupDate).getTime() : 0;
                                            const dateB = b.signupDate ? new Date(b.signupDate).getTime() : 0;
                                            return dateA - dateB;
                                        }).map(participant => {
                                            const signupDate = participant.signupDate ? new Date(participant.signupDate) : null;
                                            const today = new Date();
                                            const isToday = signupDate &&
                                                signupDate.getDate() === today.getDate() &&
                                                signupDate.getMonth() === today.getMonth() &&
                                                signupDate.getFullYear() === today.getFullYear();
                                            const stageCount = participantStages[participant.id] || 0;
                                            // Fee starts: per Lag for competitions, once for trainings.
                                            const feeStarts = requiredStarts(convention, stageCount);

                                            const hasSignupProduct = convention.signupProduct > 0;
                                            const paidStartsCount = participant.paidStartsCount || 0;
                                            const fullyPaid = hasSignupProduct && feeStarts > 0 && paidStartsCount >= feeStarts;
                                            const partiallyPaid = hasSignupProduct && paidStartsCount > 0 && paidStartsCount < feeStarts;
                                            const unpaid = hasSignupProduct && paidStartsCount === 0;
                                            let badgeClass = 'text-bg-info';
                                            let paymentTitle = '';
                                            if (hasSignupProduct) {
                                                if (fullyPaid) {
                                                    badgeClass = 'text-bg-success';
                                                    paymentTitle = ` - ${paidStartsCount} av ${feeStarts} starter betalt`;
                                                } else if (partiallyPaid) {
                                                    badgeClass = 'text-bg-info';
                                                    paymentTitle = ` - ${paidStartsCount} av ${feeStarts} starter betalt`;
                                                } else if (unpaid) {
                                                    badgeClass = 'text-bg-warning';
                                                    paymentTitle = ` - 0 av ${feeStarts} starter betalt`;
                                                }
                                            }
                                            const paymentIcon = fullyPaid ? 'fa-circle-check' : (partiallyPaid ? 'fa-coins' : 'fa-clock');

                                            return (
                                                <span
                                                    class={`badge ${badgeClass} rounded px-2 py-1`}
                                                    style='font-size: 0.8rem; font-weight: 400;'
                                                    title={`${localUtil.displayName(participant)} - ${stageCount} lag${paymentTitle}`}
                                                >
                                                    {isConventionAdmin && hasSignupProduct && (
                                                        <i class={`fa-solid ${paymentIcon} me-1`} />
                                                    )}
                                                    {isConventionAdmin && signupDate && (
                                                        <span class='opacity-75 me-1'>
                                                            {isToday ? util.isoTime(participant.signupDate) : `${signupDate.getDate()}.${signupDate.toLocaleString('nb-NO', { month: 'short' })} ${util.isoTime(participant.signupDate)}`}:
                                                        </span>
                                                    )}
                                                    <span style='font-stretch: condensed;'>{localUtil.displayName(participant)}</span>
                                                    <span class='opacity-75 ms-1'>
                                                        ({hasSignupProduct ? `${paidStartsCount}/${feeStarts}` : stageCount})
                                                    </span>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </> : <></>}
                            </>}


                            {!!user.id && !convention.hideStages && convention.stages && convention.stages.length ? <>
                                {convention.stages.map((stage, idx) => {
                                    const isAllowedToEdit = (editEnabled
                                        && !parseInt(stage.stageClosed, 10)
                                        && !parseInt(stage.stageDone, 10)
                                        && !convention.isAdminScoring);
                                    const isFieldShooting = this.isFieldShooting(stage.type);
                                    const isT96Stage = conventionResultStore.isT96(stage.type);
                                    const participants = convention[`participants${idx + 1}`];
                                    const isSignedUpToStage = participants && participants.some(participant => participant.id === user.id);
                                    const stageDone = parseInt(stage.stageDone, 10);
                                    const stageClosed = parseInt(stage.stageClosed, 10);

                                    // Focus mode shows only stages still being shot — done/paused
                                    // Lags are hidden, but a running Lag stays visible even after
                                    // the convention's scheduled end time.
                                    if (focusModeActive && (stageDone || stageClosed)) {
                                        return null;
                                    }

                                    if (isT96Stage) {
                                        const isYouOnStage = participants && participants.some(p => p.id === user.id);
                                        const isAllowToEditScore = (convention.isAdminScoring
                                            ? isConventionAdmin
                                            : (isConventionAdmin || isYouOnStage) && !stageDone);
                                        const filteredParticipants = (participants || []).filter(p =>
                                            this.filterParticipants(p, isAdminRole, stageDone, stageClosed)
                                        );
                                        return (
                                            <div key={stage.md5}>
                                                <div class='px-3 d-flex flex-row justify-content-between align-items-center mb-1 mt-3' style='font-size: 1rem;'>
                                                    <span style='font-weight: 500;'>
                                                        {stage.name}
                                                        <span class='text-body-secondary ms-2' style='font-size: 0.8rem;'>({stage.type})</span>
                                                    </span>
                                                    <div class='d-flex align-items-center'>
                                                        {signedUp && !isSignedUpToStage && isRegistrationOpen && !!convention.registrationIsOpen && !convention.isApproved && !stageDone && (
                                                            <button
                                                                class='btn btn-sm btn-primary rounded-pill px-2 py-0'
                                                                style='font-size: 0.75rem;'
                                                                data-md5={stage.md5}
                                                                onClick={this.addMe}
                                                            >
                                                                <i class='fa-duotone fa-user-plus' /> <Text id='convention.add-me'>Add me</Text>
                                                            </button>
                                                        )}
                                                        {isConventionAdmin && (
                                                            <button
                                                                class='btn btn-sm btn-outline-primary rounded-pill ms-2 px-2 py-0'
                                                                style='font-size: 0.75rem;'
                                                                data-id={convention.id}
                                                                data-md5={stage.md5}
                                                                data-idx={idx}
                                                                onClick={this.editStage}
                                                            >
                                                                <i class='fa-duotone fa-edit' />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div class='d-flex flex-column mt-0 mb-3 mx-3 px-2 py-2 rounded-lg bg-body-secondary overflow-auto'>
                                                    <div class='d-flex flex-row justify-content-start align-items-center mb-1' style='font-size: 0.85rem;'>
                                                        {isInThePast || stageDone ? (
                                                            <i class='fa-solid fa-check me-1 text-success' style='font-size: 0.8rem;' />
                                                        ) : (
                                                            <i class='fa-regular fa-clock me-1 text-body-secondary' style='font-size: 0.8rem;' />
                                                        )}
                                                        <span>{getStageTimeLabel(stage, convention)}</span>
                                                        <span class='text-body-secondary ms-2' style='font-size: 0.8rem;'>
                                                            <i class='fa-regular fa-person me-1' />{participants?.length}/{stage?.stageSlots}
                                                        </span>

                                                        {isInThePast || stageDone ? (
                                                            <span class='badge text-bg-success rounded-pill ms-2' style='font-size: 0.7rem; font-weight: 500;'>Ferdig</span>
                                                        ) : (
                                                            stageClosed ? (
                                                                <span class='badge text-bg-warning rounded-pill ms-2' style='font-size: 0.7rem; font-weight: 500;'>Venter</span>
                                                            ) : (
                                                                <span class='badge text-bg-danger rounded-pill ms-2' style='font-size: 0.7rem; font-weight: 500;'>Pågår</span>
                                                            )
                                                        )}

                                                        {isConventionAdmin && (
                                                            <div class='ms-auto d-flex align-items-center'>
                                                                {!stageDone && (
                                                                    <button
                                                                        class={`btn ${stageClosed ? 'btn-success' : 'btn-outline-warning'} rounded-pill me-2 px-3 py-1`}
                                                                        style='font-size: 0.9rem; min-height: 40px;'
                                                                        data-md5={stage.md5}
                                                                        data-field='stageClosed'
                                                                        data-value={stageClosed ? 0 : 1}
                                                                        onClick={this.toggleStageField}
                                                                        title={stageClosed ? 'Start laget' : 'Pause laget'}
                                                                    >
                                                                        <i class={`fa-solid ${stageClosed ? 'fa-play' : 'fa-pause'} me-1`} />
                                                                        {stageClosed ? 'Start' : 'Pause'}
                                                                    </button>
                                                                )}
                                                                {!stageClosed && (
                                                                    <button
                                                                        class={`btn ${stageDone ? 'btn-outline-secondary' : 'btn-success'} rounded-pill px-3 py-1`}
                                                                        style='font-size: 0.9rem; min-height: 40px;'
                                                                        data-md5={stage.md5}
                                                                        data-field='stageDone'
                                                                        data-value={stageDone ? 0 : 1}
                                                                        onClick={this.toggleStageField}
                                                                        title={stageDone ? 'Åpne laget igjen' : 'Marker som ferdig'}
                                                                    >
                                                                        <i class={`fa-solid ${stageDone ? 'fa-rotate-left' : 'fa-check'} me-1`} />
                                                                        {stageDone ? 'Åpne' : 'Ferdig'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {isConventionAdmin && firstWaitingStage && firstWaitingStage.md5 === stage.md5 && (
                                                        <div class='alert alert-warning d-flex align-items-center mt-2 mb-2 py-2' style='font-size: 1rem;'>
                                                            <i class='fa-solid fa-triangle-exclamation me-3' style='font-size: 2rem;' />
                                                            <div>
                                                                <div class='fw-bold'><Text id='convention.stage-not-started-warning-title'>The Lag is not started</Text></div>
                                                                <Text id='convention.stage-not-started-warning'>Press Start before the shooting begins, otherwise the shots are not registered on the Lag.</Text>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <ConventionViewStageT96
                                                        stores={this.props.stores}
                                                        stage={stage}
                                                        idx={idx}
                                                        participants={filteredParticipants}
                                                        isAllowedToEdit={isAllowedToEdit}
                                                        isAllowToEditScore={isAllowToEditScore}
                                                        updateResult={this.updateResult}
                                                        handleFocus={this.handleFocus}
                                                        addRef={this.addRef}
                                                        editParticipant={this.editParticipant}
                                                        drawerLevel={this.props.drawerLevel}
                                                    />

                                                    {isConventionAdmin && !focusModeActive && !stageDone && idx >= 0 && (
                                                        <>
                                                            <div class='text-body-secondary mt-3 mb-1' style='font-size: 0.8rem;'>
                                                                <Text id='convention.admin-add-new-user'>Admin add new user to stage</Text>:
                                                            </div>
                                                            {isInThePast ? (
                                                                <div class='d-flex text-body-secondary' style='font-size: 0.85rem;'>
                                                                    <Text id='convention.in-the-past'>Convention is in the past and closed.</Text>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    {(() => {
                                                                        // Only participants not yet on this stage, sorted by stage count then signup date
                                                                        const missingParticipants = (convention.participants || []).filter(p =>
                                                                            !participants?.some(pp => pp.id === p.id)
                                                                        ).sort((a, b) => {
                                                                            const stagesA = participantStages[a.id] || 0;
                                                                            const stagesB = participantStages[b.id] || 0;
                                                                            if (stagesA !== stagesB) return stagesA - stagesB;
                                                                            const dateA = a.signupDate ? new Date(a.signupDate).getTime() : 0;
                                                                            const dateB = b.signupDate ? new Date(b.signupDate).getTime() : 0;
                                                                            return dateA - dateB;
                                                                        });

                                                                        // Group by stage count so each group gets its own "N lag" header
                                                                        const stageCountGroups = [];
                                                                        missingParticipants.forEach(p => {
                                                                            const stageCount = participantStages[p.id] || 0;
                                                                            let group = stageCountGroups[stageCountGroups.length - 1];
                                                                            if (!group || group.stageCount !== stageCount) {
                                                                                group = { stageCount, participants: [] };
                                                                                stageCountGroups.push(group);
                                                                            }
                                                                            group.participants.push(p);
                                                                        });

                                                                        return stageCountGroups.map(group => {
                                                                            const isUnassigned = group.stageCount === 0;
                                                                            return (
                                                                            <div class={`mb-2 ${isUnassigned ? 'p-2 rounded border border-success-subtle bg-success-subtle' : ''}`}>
                                                                                <div class={`border-bottom mb-1 pb-1 ${isUnassigned ? 'text-success-emphasis border-success-subtle' : 'text-body-secondary'}`} style={`font-size: ${isUnassigned ? '0.95rem' : '0.7rem'};`}>
                                                                                    {isUnassigned && <i class='fa-solid fa-user-plus me-1' />}
                                                                                    <span class={isUnassigned ? 'fw-bold' : 'fw-semibold text-body'}>
                                                                                        <Text id='convention.stage-count-group' fields={{ count: group.stageCount }}>{group.stageCount} lag</Text>
                                                                                    </span>
                                                                                    <span class='ms-1 fst-italic'>
                                                                                        {group.stageCount === 0
                                                                                            ? <Text id='convention.stage-count-group-desc-none'>– ikke satt på noe lag ennå</Text>
                                                                                            : <Text id='convention.stage-count-group-desc-some' fields={{ count: group.stageCount }}>– allerede på {group.stageCount} lag</Text>}
                                                                                    </span>
                                                                                </div>
                                                                                <div class='d-flex flex-wrap gap-2'>
                                                                                    {group.participants.map(p => {
                                                                                        const signupDate = p.signupDate ? new Date(p.signupDate) : null;
                                                                                        const today = new Date();
                                                                                        const isToday = signupDate
                                                                                            && signupDate.getDate() === today.getDate()
                                                                                            && signupDate.getMonth() === today.getMonth()
                                                                                            && signupDate.getFullYear() === today.getFullYear();

                                                                                        const stageCount = participantStages[p.id] || 0;
                                                                                        const feeStarts = requiredStarts(convention, stageCount);
                                                                                        const hasSignupProduct = convention.signupProduct > 0;
                                                                                        const paidStartsCount = p.paidStartsCount || 0;
                                                                                        const fullyPaid = hasSignupProduct && feeStarts > 0 && paidStartsCount >= feeStarts;
                                                                                        const partiallyPaid = hasSignupProduct && paidStartsCount > 0 && paidStartsCount < feeStarts;
                                                                                        let paymentTitle = '';
                                                                                        if (hasSignupProduct) {
                                                                                            paymentTitle = ` - ${paidStartsCount} av ${feeStarts} starter betalt`;
                                                                                        }
                                                                                        const paymentIcon = fullyPaid ? 'fa-circle-check text-success' : (partiallyPaid ? 'fa-coins text-info' : 'fa-clock text-warning');

                                                                                        return (
                                                                                            <button
                                                                                                class='btn btn-outline-primary rounded-pill px-3 py-2'
                                                                                                style='font-size: 0.9rem; min-height: 40px;'
                                                                                                onClick={this.addParticipant}
                                                                                                data-id={p.id}
                                                                                                data-md5={stage.md5}
                                                                                                title={`Legg til ${localUtil.displayName(p)} - ${stageCount} lag${paymentTitle}`}
                                                                                            >
                                                                                                <i class='fa-solid fa-plus me-1' style='font-size: 0.7rem;' />
                                                                                                {hasSignupProduct && (
                                                                                                    <i class={`fa-solid ${paymentIcon} me-1`} style='font-size: 0.7rem;' />
                                                                                                )}
                                                                                                {signupDate && (
                                                                                                    <span class='text-body-secondary me-1'>
                                                                                                        {isToday ? util.isoTime(p.signupDate) : `${signupDate.getDate()}.${signupDate.toLocaleString('nb-NO', { month: 'short' })} ${util.isoTime(p.signupDate)}`}
                                                                                                    </span>
                                                                                                )}
                                                                                                <span style='font-stretch: condensed;'>{localUtil.displayName(p)}</span>
                                                                                                <span class='text-body-secondary ms-1'>
                                                                                                    ({hasSignupProduct ? `${paidStartsCount}/${feeStarts}` : stageCount})
                                                                                                </span>
                                                                                            </button>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (<>
                                        <div class='px-3 d-flex flex-row justify-content-between align-items-center mb-1 mt-3' style='font-size: 1rem;'>
                                            <span style='font-weight: 500;'>{stage.name} <span class='text-muted' style='font-size: 0.8rem;'>({stage.type})</span></span>
                                            <div class='d-flex align-items-center'>
                                                {signedUp && !isSignedUpToStage && isRegistrationOpen && !!convention.registrationIsOpen && !convention.isApproved && !stageDone && (
                                                    <button
                                                        class='btn btn-sm btn-primary rounded-pill px-2 py-0'
                                                        style='font-size: 0.75rem;'
                                                        data-md5={stage.md5}
                                                        onClick={this.addMe}
                                                    >
                                                        <i class='fa-duotone fa-user-plus' /> <Text id='convention.add-me'>Add me</Text>
                                                    </button>
                                                )}
                                                {/* Alle/Meg toggle is admin-only. Normal users always see just their
                                                    own scorecard, so the toggle would be a no-op for them. */}
                                                {isAdminRole && !stageDone && !stageClosed && (
                                                    <button
                                                        class={`btn btn-sm btn-${showAll ? 'outline-secondary' : 'primary'} rounded-pill ms-2 px-2 py-0`}
                                                        style='font-size: 0.75rem;'
                                                        onClick={this.toggleShowAll}
                                                        title={showAll ? 'Vis kun meg' : 'Vis alle deltakere'}
                                                    >
                                                        <i class={`fa-duotone ${showAll ? 'fa-user' : 'fa-users'} me-1`} />
                                                        {showAll ? 'Meg' : 'Alle'}
                                                    </button>
                                                )}
                                                {isConventionAdmin && (
                                                    <button
                                                        class='btn btn-sm btn-outline-primary rounded-pill ms-2 px-2 py-0'
                                                        style='font-size: 0.75rem;'
                                                        data-id={convention.id}
                                                        data-md5={stage.md5}
                                                        data-idx={idx}
                                                        onClick={this.editStage}
                                                    >
                                                        <i class='fa-duotone fa-edit' />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div class={`d-flex flex-column mt-0 mb-3 mx-3 px-2 py-2 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto`}>
                                            <div class='d-flex flex-row justify-content-start align-items-center mb-1' style='font-size: 0.85rem;'>
                                                {isInThePast || stageDone ? (
                                                    <i class='fa-solid fa-check me-1 text-success' style='font-size: 0.8rem;' />
                                                ) : (
                                                    <i class='fa-regular fa-clock me-1 text-muted' style='font-size: 0.8rem;' />
                                                )}
                                                <span>{getStageTimeLabel(stage, convention)}</span>
                                                <span class='text-muted ms-2' style='font-size: 0.8rem;'>
                                                    <i class='fa-regular fa-person me-1' />{participants?.length}/{stage?.stageSlots}
                                                </span>

                                                {isInThePast || stageDone ? (
                                                    <span class='badge text-bg-success rounded-pill ms-2' style='font-size: 0.7rem; font-weight: 500;'>Ferdig</span>
                                                ) : (
                                                    stageClosed ? (
                                                        <span class='badge text-bg-warning rounded-pill ms-2' style='font-size: 0.7rem; font-weight: 500;'>Venter</span>
                                                    ) : (
                                                        <span class='badge text-bg-danger rounded-pill ms-2' style='font-size: 0.7rem; font-weight: 500;'>Pågår</span>
                                                    )
                                                )}

                                                {isConventionAdmin && (
                                                    <div class='ms-auto d-flex align-items-center'>
                                                        {!stageDone && (
                                                            <button
                                                                class={`btn ${stageClosed ? 'btn-success' : 'btn-outline-warning'} rounded-pill me-2 px-3 py-1`}
                                                                style='font-size: 0.9rem; min-height: 40px;'
                                                                data-md5={stage.md5}
                                                                data-field='stageClosed'
                                                                data-value={stageClosed ? 0 : 1}
                                                                onClick={this.toggleStageField}
                                                                title={stageClosed ? 'Start laget' : 'Pause laget'}
                                                            >
                                                                <i class={`fa-solid ${stageClosed ? 'fa-play' : 'fa-pause'} me-1`} />
                                                                {stageClosed ? 'Start' : 'Pause'}
                                                            </button>
                                                        )}
                                                        {!stageClosed && (
                                                            <button
                                                                class={`btn ${stageDone ? 'btn-outline-secondary' : 'btn-success'} rounded-pill px-3 py-1`}
                                                                style='font-size: 0.9rem; min-height: 40px;'
                                                                data-md5={stage.md5}
                                                                data-field='stageDone'
                                                                data-value={stageDone ? 0 : 1}
                                                                onClick={this.toggleStageField}
                                                                title={stageDone ? 'Åpne laget igjen' : 'Marker som ferdig'}
                                                            >
                                                                <i class={`fa-solid ${stageDone ? 'fa-rotate-left' : 'fa-check'} me-1`} />
                                                                {stageDone ? 'Åpne' : 'Ferdig'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {isConventionAdmin && firstWaitingStage && firstWaitingStage.md5 === stage.md5 && (
                                                <div class='alert alert-warning d-flex align-items-center mt-2 mb-2 py-2' style='font-size: 1rem;'>
                                                    <i class='fa-solid fa-triangle-exclamation me-3' style='font-size: 2rem;' />
                                                    <div>
                                                        <div class='fw-bold'><Text id='convention.stage-not-started-warning-title'>The Lag is not started</Text></div>
                                                        <Text id='convention.stage-not-started-warning'>Press Start before the shooting begins, otherwise the shots are not registered on the Lag.</Text>
                                                    </div>
                                                </div>
                                            )}
                                            <div class='table-responsive w-100'>
                                                {convention.isAdminScoring && (
                                                    <div class='alert alert-secondary text-center py-2 my-2' style='font-size: 0.85rem;'>
                                                        <i class='fa-duotone fa-lock me-2' />
                                                        {isConventionAdmin
                                                            ? <Text id='convention.scores-admin-only-admin'>Poengsum registreres fra Score-siden.</Text>
                                                            : <Text id='convention.scores-admin-only'>Poengsum administreres av banefunksjonær.</Text>}
                                                    </div>
                                                )}
                                                <table class='table table-sm table-striped mb-0' style='font-size: 0.85rem;'>
                                                    <thead>
                                                        <tr style='font-size: 0.7rem;'>
                                                            <th class='text-muted py-1 px-2' style='font-weight: 400;'>&nbsp;</th>

                                                            {isFieldShooting && !isAllowedToEdit ? <>
                                                                {generateArray(stage.stages || 10).map(stand => (
                                                                    <th class='text-muted text-center py-1 px-1' style='font-weight: 400;'>{stand}</th>
                                                                ))}
                                                            </> : <>
                                                                {this.isFieldShooting(stage.type) ? <>
                                                                    <th class='text-muted text-center py-1 px-1' style='font-weight: 400;'><i class='fa-solid fa-crosshairs-simple' /></th>
                                                                    <th class='text-muted text-center py-1 px-1' style='font-weight: 400;'><i class='fa-solid fa-image' /></th>
                                                                </> : <></>}
                                                                {conventionResultStore.getParticipantFields(stage).map((field, index) => (
                                                                    <th class='text-muted py-1 px-1' style='font-weight: 400;'>{field}</th>
                                                                ))}
                                                            </>}

                                                            {this.isFieldShooting(stage.type) && !isAllowedToEdit ? <>
                                                                <th class='text-muted text-center py-1 px-1' style='font-weight: 400;'>iX</th>
                                                            </> : <></>}
                                                            <th class='text-muted text-center py-1 px-1' style='font-weight: 400;'>Tot</th>
                                                            {(stage.type === 'nais' || stage.type === 'hurtig') ? <>
                                                                <th class='text-muted text-center py-1 px-1' style='font-weight: 400;'>Merke</th>
                                                            </> : <></>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {participants && participants.filter(p => this.filterParticipants(p, isAdminRole, stageDone, stageClosed)).map((participant, participantIdx) => {
                                                            const participantResults = conventionResults.filter(result => result.participant === participant.id && result.stage === stage.md5);
                                                            let totalScore = 0;
                                                            let totalIx = 0;
                                                            let standScore = 0;
                                                            const isYou = participant.id === user.id;
                                                            const isAllowedToEditSignup = isAdmin || isYou;
                                                            const isAllowToEditScore = (convention.isAdminScoring ? isConventionAdmin : (isConventionAdmin || isYou) && !stageDone);
                                                            let participantDiffToMax = 0;
                                                            let participantDiffToMaxInner = 0;
                                                            let participantMaxScore = 0;
                                                            let participantMaxInner = 0;

                                                            const participantMaxShots = ['sp.rev', 'sp.pistol', 'mag1', 'mag2'].includes(participant.class) ? 5 : 6;

                                                            if (!isAllowedToEdit && isFieldShooting) {
                                                                // Calculate all stand scores first
                                                                const standResults = generateArray(stage.stages || 10).map(stand => {
                                                                    let score = 0;
                                                                    const targetResult = participantResults.find(result => result.stand === stand);
                                                                    score += targetResult?.scoreHits || 0;
                                                                    score += targetResult?.scoreTargets || 0;
                                                                    if (targetResult?.scorePenalty) {
                                                                        score -= targetResult.scorePenalty;
                                                                    }
                                                                    totalScore += score;

                                                                    const inner = targetResult?.scoreInner || 0;
                                                                    totalIx += inner;

                                                                    const currentStand = parseInt(stand, 10);
                                                                    let currentMaxValues = {};
                                                                    if (stage.type === 'norgesfelt') {
                                                                        currentMaxValues = norgesFeltTargetMaxValues.find(item => item.stand === currentStand);
                                                                    } else if (stage.type === 'felt') {
                                                                        currentMaxValues = stageStands.find(item => parseInt(item.stand, 10) === currentStand);
                                                                    }
                                                                    // Felt without explicit stageStands has no predefined limits; treat as 0 for max-score totals
                                                                    const feltUncapped = stage.type === 'felt' && !currentMaxValues;
                                                                    let maxHits = parseInt(currentMaxValues?.scoreHits, 10) || (feltUncapped ? 0 : 6);
                                                                    let maxTargets = parseInt(currentMaxValues?.scoreTargets, 10) || (feltUncapped ? 0 : 6);
                                                                    let maxInner = parseInt(currentMaxValues?.scoreInner, 10) || (feltUncapped ? 0 : 6);
                                                                    if (participantMaxShots < 6) {
                                                                        maxTargets = maxTargets > participantMaxShots ? participantMaxShots : maxTargets;
                                                                        maxInner = participantMaxShots;
                                                                        maxHits = participantMaxShots;
                                                                    }
                                                                    let maxScore = parseInt(maxHits + maxTargets, 10) || 0;

                                                                    participantDiffToMax += score - maxScore;
                                                                    participantDiffToMaxInner += inner - maxInner;
                                                                    participantMaxScore += maxScore;
                                                                    participantMaxInner += maxInner;

                                                                    return { stand, score, inner, maxScore, maxInner, targetResult };
                                                                });

                                                                return(<>
                                                                    <tr style='font-size: 0.85rem;'>
                                                                        <td
                                                                            class='py-1 px-1'
                                                                            style={`max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-stretch: condensed; ${isAllowedToEditSignup && !stageDone ? 'cursor: pointer;' : ''}`}
                                                                            title={`${localUtil.displayName(participant)} - ${participant.class || '?'}`}
                                                                            data-id={convention.id}
                                                                            data-md5={participant.md5}
                                                                            data-stage={stage.md5}
                                                                            data-idx={idx}
                                                                            onClick={isAllowedToEditSignup && !stageDone ? this.editParticipant : undefined}
                                                                        >
                                                                            {isAllowedToEditSignup && !stageDone && (
                                                                                <i class='fa-solid fa-edit me-1 text-muted' style='font-size: 0.7rem;' />
                                                                            )}
                                                                            <span style='font-weight: 500;'>{localUtil.displayName(participant)}</span>
                                                                            <span class='text-muted ms-1' style='font-size: 0.75rem;'>{participant.class || '?'}</span>
                                                                            <ShooterMarkBadges stores={this.props.stores} conventionId={convention.id} userId={participant.id} userName={localUtil.displayName(participant)} canEdit={isMarkOfficial} />
                                                                        </td>
                                                                        {standResults.map(({ stand, score, inner, maxScore, maxInner, targetResult }) => (
                                                                            <td
                                                                                class='text-center py-1 px-1'
                                                                                style='cursor: pointer; font-size: 0.8rem;'
                                                                                data-id={targetResult?.id}
                                                                                onClick={this.viewImage}
                                                                                title={`Stand ${stand}: ${score}/${inner}`}
                                                                            >
                                                                                <span class={(maxScore > 0 && score > maxScore) ? 'text-danger' : (score > 0 ? 'text-success' : '')}>{score}</span>
                                                                                <span class='text-muted'>/</span>
                                                                                <span class={(maxInner > 0 && inner > maxInner) ? 'text-danger' : (inner > 0 ? 'text-success' : 'text-muted')}>{inner}</span>
                                                                            </td>
                                                                        ))}
                                                                        <td class='text-center py-1 px-1 text-muted' style='font-size: 0.85rem;' title={`IX: ${totalIx} / max ${participantMaxInner}`}>
                                                                            {totalIx}
                                                                        </td>
                                                                        <td class='text-center py-1 px-2' style='font-weight: 700; font-size: 0.95rem;' title={`Total: ${totalScore} / max ${participantMaxScore}`}>
                                                                            {totalScore}
                                                                        </td>
                                                                    </tr>
                                                                </>);
                                                            }

                                                            if (isFieldShooting) {
                                                                const totalStands = stage.stages || 10;
                                                                return (<>
                                                                    {participantIdx > 0 && <>
                                                                        <tr>
                                                                            <th class='text-muted'><small>&nbsp;</small></th>
                                                                            {isFieldShooting && !isAllowedToEdit ? <>
                                                                                {generateArray(stage.stages || 10).map(stand => {
                                                                                    return (<>
                                                                                        <th class='text-muted text-center w-5'><small>{stand}</small></th>
                                                                                    </>);
                                                                                })}
                                                                            </> : <>
                                                                                {this.isFieldShooting(stage.type) ? <>
                                                                                    <th class='text-muted text-center'><small><i class='fa-solid fa-crosshairs-simple' /></small></th>
                                                                                    <th class='text-muted text-center w-5'><small><i class='fa-solid fa-image' /></small></th>
                                                                                </> : <></>}
                                                                                {conventionResultStore.getParticipantFields(stage).map((field, index) => {
                                                                                    return (<>
                                                                                        <th class='text-muted font-weight-lighter w-5'><small>{field}</small></th>
                                                                                    </>);
                                                                                })}
                                                                            </>}
                                                                            {/* {this.isFieldShooting(stage.type) ? <>
                                                                                <th class='text-muted text-center w-5'><small><Text id='input.innerX'>iX</Text></small></th>
                                                                            </> : <></>} */}
                                                                            <th class='text-muted text-center w-5'><small><Text id='input.totalScore'>Total</Text></small></th>
                                                                            <th class='text-muted text-center w-5'><small><i class='fa-duotone fa-camera' /></small></th>
                                                                        </tr>
                                                                    </>}

                                                                    {(!participant.class && isAllowedToEditSignup) ? <>
                                                                        <tr>
                                                                            <td class='p-0 middle' colspan='10'>

                                                                                <div class='bg-danger text-white rounded-lg p-3 my-3'>
                                                                                    <span class='me-3' style='font-size: 2.0em;'><i class="fa-solid fa-hand" /></span>
                                                                                    <span style='font-size: 1.3em;'><Text id='convention.no-class-info'>No class selected!</Text></span>
                                                                                    <button
                                                                                        class='btn btn-primary btn-block rounded-pill mt-2'
                                                                                        data-id={convention.id}
                                                                                        data-md5={participant.md5}
                                                                                        data-stage={stage.md5}
                                                                                        data-idx={idx}
                                                                                        onClick={this.editParticipant}
                                                                                    >
                                                                                        <i class='fa-solid fa-pen' /> <Text id='convention.add-shooting-class'>Add shooting class</Text>
                                                                                    </button>
                                                                                </div>

                                                                            </td>
                                                                        </tr>
                                                                    </> : <></>}

                                                                    {generateArray(totalStands).map(stand => {
                                                                        standScore = 0;
                                                                        const targetResult = participantResults.find(result => result.stand === stand);
                                                                        const standHits = targetResult?.scoreHits || 0;
                                                                        const standTargets = targetResult?.scoreTargets || 0;
                                                                        standScore += standHits;
                                                                        standScore += targetResult?.scoreTargets || 0;
                                                                        if (targetResult?.scorePenalty) {
                                                                            standScore -= targetResult.scorePenalty;
                                                                        }
                                                                        totalScore += standScore;

                                                                        const standInner = targetResult?.scoreInner || 0;
                                                                        totalIx += standInner;

                                                                        const standIdx = parseInt(stand, 10) - 1;
                                                                        const standImage = convention.stageStandImages ? convention.stageStandImages[standIdx] : null;

                                                                        const currentStand = parseInt(stand, 10);
                                                                        let currentMaxValues = {};
                                                                        if (stage.type === 'norgesfelt') {
                                                                            currentMaxValues = norgesFeltTargetMaxValues.find(item => item.stand === currentStand);
                                                                        } else if (stage.type === 'felt') {
                                                                            currentMaxValues = stageStands.find(item => parseInt(item.stand, 10) === currentStand);
                                                                        }
                                                                        // Felt without explicit stageStands has no predefined limits
                                                                        const feltUncapped = stage.type === 'felt' && !currentMaxValues;
                                                                        let maxHits = parseInt(currentMaxValues?.scoreHits, 10) || (feltUncapped ? 0 : 6);
                                                                        let maxTargets = parseInt(currentMaxValues?.scoreTargets, 10) || (feltUncapped ? 0 : 6);
                                                                        const maxGroups = parseInt(currentMaxValues?.scoreGroups, 10) || 0;
                                                                        let maxInner = parseInt(currentMaxValues?.scoreInner, 10) || (feltUncapped ? 0 : 6);
                                                                        if (participantMaxShots < 6) {
                                                                            maxTargets = maxTargets > participantMaxShots ? participantMaxShots : maxTargets;
                                                                            maxInner = participantMaxShots;
                                                                            maxHits = participantMaxShots;
                                                                        }
                                                                        const maxScore = parseInt(maxHits + maxTargets, 10) || 0;


                                                                        const diffToMax = standScore - maxScore;
                                                                        const diffToMaxInner = standInner - maxInner;

                                                                        participantDiffToMax += diffToMax;
                                                                        participantDiffToMaxInner += diffToMaxInner;
                                                                        participantMaxScore += maxScore;
                                                                        participantMaxInner += maxInner;

                                                                        return (<>
                                                                            <tr>
                                                                                {isAllowedToEdit ? <>
                                                                                    {stand === 1 && <>
                                                                                        <td
                                                                                            class='p-1'
                                                                                            rowspan={totalStands}
                                                                                            style='background-color: rgba(0,0,255,0.1); font-stretch: condensed;'
                                                                                            title={`${localUtil.displayName(participant)} - ${participant.class || '?'}`}
                                                                                            data-id={convention.id}
                                                                                            data-md5={participant.md5}
                                                                                            data-stage={stage.md5}
                                                                                            data-idx={idx}
                                                                                        >
                                                                                            <div class='vertical-text' style='white-space: nowrap;'>
                                                                                                <span style='font-weight: 500;'>{localUtil.displayName(participant)}</span>
                                                                                                <span class='text-muted ms-1' style='font-size: 0.75rem;'>{participant.class || '?'}</span>
                                                                                                <ShooterMarkBadges stores={this.props.stores} conventionId={convention.id} userId={participant.id} userName={localUtil.displayName(participant)} canEdit={isMarkOfficial} />
                                                                                                {isAllowedToEditSignup && (
                                                                                                    <i
                                                                                                        class='fa-solid fa-edit ms-1 text-muted'
                                                                                                        style='font-size: 0.7rem; cursor: pointer;'
                                                                                                        onClick={this.editParticipant}
                                                                                                    />
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                    </>}
                                                                                </> : <>
                                                                                    <td
                                                                                        class='py-1 px-1'
                                                                                        style={`max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-stretch: condensed; ${isAllowedToEditSignup ? 'cursor: pointer;' : ''}`}
                                                                                        title={`${localUtil.displayName(participant)} - ${participant.class || '?'}`}
                                                                                        data-id={convention.id}
                                                                                        data-md5={participant.md5}
                                                                                        data-stage={stage.md5}
                                                                                        data-idx={idx}
                                                                                        onClick={isAllowedToEditSignup ? this.editParticipant : undefined}
                                                                                    >
                                                                                        {isAllowedToEditSignup && (
                                                                                            <i class='fa-solid fa-edit me-1 text-muted' style='font-size: 0.7rem;' />
                                                                                        )}
                                                                                        <span style='font-weight: 500;'>{localUtil.displayName(participant)}</span>
                                                                                        <span class='text-muted ms-1' style='font-size: 0.75rem;'>{participant.class || '?'}</span>
                                                                                        <ShooterMarkBadges stores={this.props.stores} conventionId={convention.id} userId={participant.id} userName={localUtil.displayName(participant)} canEdit={isMarkOfficial} />
                                                                                    </td>
                                                                                </>}
                                                                                <td
                                                                                    class='text-center font-weight-lighter middle p-0 position-relative'
                                                                                    style={'background-color: rgba(0,0,255,0.1);'}
                                                                                >
                                                                                    {stand}
                                                                                    {(() => {
                                                                                        const standInfo = getStandInfo(convention, stand);
                                                                                        const scoreCfg = getStandScoreConfig(convention, stand);
                                                                                        const magnum = getStandMagnumInfo(convention, stand);
                                                                                        if (!standInfo && !scoreCfg && !magnum) return null;
                                                                                        const magnumSummary = formatStandMagnumSummary(magnum);
                                                                                        const magnumText = magnumSummary ? `Magnum: ${magnumSummary}` : '';
                                                                                        const title = [
                                                                                            standInfo?.shootingTime != null ? `${standInfo.shootingTime}s` : '',
                                                                                            standInfo?.shootingPosition || '',
                                                                                            magnumText,
                                                                                            scoreCfg?.scoreTargets != null ? `${scoreCfg.scoreTargets} mål` : '',
                                                                                            scoreCfg?.scoreGroups != null ? `${scoreCfg.scoreGroups} målgrupper` : '',
                                                                                            scoreCfg?.scoreInner != null ? `${scoreCfg.scoreInner} soner` : '',
                                                                                            standInfo?.notes || '',
                                                                                        ].filter(Boolean).join(' · ');
                                                                                        return (
                                                                                            <div class='text-body-secondary' style='font-size: 0.6rem; line-height: 0.9rem;' title={title}>
                                                                                                {standInfo && standInfo.shootingTime != null && <span class='d-block'><i class='fa-duotone fa-clock me-1' />{standInfo.shootingTime}s</span>}
                                                                                                {standInfo && standInfo.shootingPosition && <span class='d-block'><i class='fa-duotone fa-person-rifle me-1' />{standInfo.shootingPosition}</span>}
                                                                                                {magnum && <span class='d-block'><i class='fa-duotone fa-bullseye-pointer me-1' /><Text id='convention.course-magnum'>Magnum</Text>: {magnumSummary}</span>}
                                                                                                {scoreCfg && scoreCfg.scoreTargets != null && <span class='d-block'><i class='fa-duotone fa-bullseye me-1' />{scoreCfg.scoreTargets}</span>}
                                                                                                {scoreCfg && scoreCfg.scoreGroups != null && <span class='d-block'><i class='fa-duotone fa-layer-group me-1' />{scoreCfg.scoreGroups}</span>}
                                                                                                {scoreCfg && scoreCfg.scoreInner != null && <span class='d-block'><i class='fa-duotone fa-bullseye-arrow me-1' />{scoreCfg.scoreInner}</span>}
                                                                                                {standInfo && standInfo.notes && <i class='fa-duotone fa-note' />}
                                                                                            </div>
                                                                                        );
                                                                                    })()}
                                                                                </td>
                                                                                <td
                                                                                    class='text-center font-weight-lighter middle p-0'
                                                                                    style={'background-color: rgba(0,0,255,0.1);'}
                                                                                >
                                                                                    {standImage && <>
                                                                                        <img src={displayImage(standImage, this.props, '220x')} class='img-fluid' style='max-height: 30px; right: 0px;' />
                                                                                    </>}
                                                                                </td>
                                                                                <td class='text-center p-0 position-relative'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max={maxHits > 0 ? maxHits : 50}
                                                                                            class={`form-control form-control-sm py-0 px-1 text-center ${standHits > maxHits ? 'bg-danger-light' : ''}`}
                                                                                            style={INPUT_STYLE}
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={300}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scoreHits'
                                                                                            value={targetResult?.scoreHits >= 0 ? targetResult?.scoreHits : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scoreHits`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scoreHits || ''}</>}
                                                                                    {maxHits > 0 && (
                                                                                        <div class='position-absolute' style={`bottom: 0px; right: 20%; font-size: 0.5em;`}>
                                                                                            <span class='font-weight-lighter'>{maxHits}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td class='text-center p-0 position-relative'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max={maxTargets > 0 ? maxTargets : 50}
                                                                                            class={`form-control form-control-sm py-0 px-1 text-center ${(standTargets > maxTargets || standTargets > standHits) ? 'bg-danger-light' : ''}`}
                                                                                            style={INPUT_STYLE}
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={300}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scoreTargets'
                                                                                            value={targetResult?.scoreTargets >= 0 ? targetResult?.scoreTargets : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scoreTargets`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scoreTargets || ''}</>}
                                                                                    {maxTargets > 0 && (
                                                                                        <div class='position-absolute' style={`bottom: 0px; right: 20%; font-size: 0.5em;`}>
                                                                                            <span class='font-weight-lighter'>{maxTargets}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td class='text-center p-0'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max={maxInner > 0 ? maxInner : 50}
                                                                                            class={`form-control form-control-sm py-0 px-1 text-center ${(standInner > maxInner || standInner > standHits) ? 'bg-danger-light' : ''}`}
                                                                                            style={INPUT_STYLE}
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={300}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scoreInner'
                                                                                            value={targetResult?.scoreInner >= 0 ? targetResult?.scoreInner : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scoreInner`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scoreInner || ''}</>}
                                                                                </td>
                                                                                <td class='text-center p-0'>
                                                                                    {isAllowedToEdit ? <>
                                                                                        <input
                                                                                            type='tel'
                                                                                            pattern='[0-9]*'
                                                                                            inputmode='numeric'
                                                                                            min='0'
                                                                                            max='6'
                                                                                            class='form-control form-control-sm py-0 px-1 text-danger text-center'
                                                                                            style={INPUT_STYLE}
                                                                                            size='2'
                                                                                            data-participant={participant.id}
                                                                                            data-id={targetResult?.id}
                                                                                            data-stage={stage.md5}
                                                                                            data-type={stage.type}
                                                                                            data-stand={stand}
                                                                                            data-delay={300}
                                                                                            onInput={this.updateResult}
                                                                                            onFocus={this.handleFocus}
                                                                                            // onContextMenu={this.handleContextMenu}
                                                                                            name='scorePenalty'
                                                                                            value={targetResult?.scorePenalty >= 0 ? targetResult?.scorePenalty : ''}
                                                                                            disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                            ref={c => this.refs[`${participant.id}-${stage.md5}-${stand}-scorePenalty`] = c}
                                                                                            autoComplete='off'
                                                                                        />
                                                                                    </> : <>{targetResult?.scorePenalty || ''}</>}
                                                                                </td>
                                                                                {/* <td class={`text-center middle p-0 position-relative ${standInner > maxInner ? 'text-danger' : ''}`}>
                                                                                    {standInner || ''}
                                                                                    <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                        <span class='font-weight-lighter'>{diffToMaxInner}</span>
                                                                                    </div>
                                                                                </td> */}
                                                                                <td class={`text-center middle p-0 position-relative ${standScore > maxScore ? 'text-danger' : ''}`}>
                                                                                    {standScore}
                                                                                    <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                        <span class='font-weight-lighter'>{diffToMax}</span>
                                                                                    </div>
                                                                                </td>
                                                                                <td class='text-center middle p-0'>
                                                                                    {/* Target photos live on the result row, so the camera only
                                                                                        appears once the cell has been saved (id exists). Editors
                                                                                        (own row / officials) get the camera → upload drawer; others
                                                                                        get a view-only icon when photos exist. */}
                                                                                    {targetResult?.id ? <>
                                                                                        {isAllowToEditScore ? <>
                                                                                            <i
                                                                                                class={`fa-duotone fa-camera ${targetResult?.images?.length > 0 ? 'text-primary' : 'text-muted'}`}
                                                                                                style='cursor: pointer; font-size: 0.9rem;'
                                                                                                onClick={this.addImage}
                                                                                                data-id={targetResult?.id}
                                                                                            />
                                                                                        </> : <>
                                                                                            {targetResult?.images?.length > 0 && (
                                                                                                <i
                                                                                                    class='fa-duotone fa-image text-muted'
                                                                                                    style='cursor: pointer; font-size: 0.9rem;'
                                                                                                    onClick={this.viewEditImage}
                                                                                                    data-id={targetResult?.id}
                                                                                                />
                                                                                            )}
                                                                                        </>}
                                                                                        {targetResult?.images?.length > 0 && (
                                                                                            <span class='text-muted ms-1' style='font-size: 0.6rem;'>{targetResult.images.length}</span>
                                                                                        )}
                                                                                    </> : <></>}
                                                                                </td>
                                                                            </tr>
                                                                        </>);
                                                                    })}
                                                                    <tr style='background-color: rgba(255,0,0,0.1); border-bottom: 2px solid #000000;'>
                                                                        <td class='text-end font-weight-bolder' colspan='5'><Text id='input.totalScore'>Total</Text></td>
                                                                        <td class='text-center font-weight-lighter position-relative'>
                                                                            {totalIx}
                                                                            <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                <span class='font-weight-lighter'>
                                                                                    {participantMaxInner}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td>&nbsp;</td>
                                                                        <td class='text-center font-weight-bolder position-relative'>
                                                                            {totalScore}
                                                                            <div class='position-absolute' style={`bottom: 0px; right: 0px; font-size: 0.5em;`}>
                                                                                <span class='font-weight-lighter'>
                                                                                    {participantMaxScore}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td class='text-center middle p-0'>&nbsp;</td>
                                                                    </tr>
                                                                </>);
                                                            }

                                                            return (<>
                                                                <tr>
                                                                    <td
                                                                        class='py-1 px-1'
                                                                        style={`max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-stretch: condensed; ${isAllowedToEditSignup ? 'cursor: pointer;' : ''}`}
                                                                        title={`${localUtil.displayName(participant)} - ${participant.class || '?'}`}
                                                                        data-id={convention.id}
                                                                        data-md5={participant.md5}
                                                                        data-stage={stage.md5}
                                                                        data-idx={idx}
                                                                        onClick={isAllowedToEditSignup ? this.editParticipant : undefined}
                                                                    >
                                                                        {isAllowedToEditSignup && (
                                                                            <i class='fa-solid fa-edit me-1 text-muted' style='font-size: 0.7rem;' />
                                                                        )}
                                                                        {hasMegalinkResults(participantResults) && this.renderShooterShotsIcon(convention, stage, participant)}
                                                                        <span style='font-weight: 500;'>{localUtil.displayName(participant)}</span>
                                                                        <span class='text-muted ms-1' style='font-size: 0.75rem;'>{participant.class || '?'}</span>
                                                                        <ShooterMarkBadges stores={this.props.stores} conventionId={convention.id} userId={participant.id} userName={localUtil.displayName(participant)} canEdit={isMarkOfficial} />
                                                                    </td>
                                                                    {conventionResultStore.getParticipantFields(stage).map((field, index) => {
                                                                        const targetResult = participantResults.find(result => result.targetIndex === index);
                                                                        if (targetResult && targetResult.scoreTotal) {
                                                                            totalScore += targetResult.scoreTotal;
                                                                        }
                                                                        const isTimeStageInput = this.isTimeStage(stage.type);
                                                                        return (<>
                                                                            <td class='text-center'>
                                                                                {isAllowedToEdit ? <>
                                                                                    <input
                                                                                        type={isTimeStageInput ? 'text' : 'tel'}
                                                                                        pattern={isTimeStageInput ? '[0-9]*\\.?[0-9]*' : '[0-9]*'}
                                                                                        inputmode={isTimeStageInput ? 'decimal' : 'numeric'}
                                                                                        step={isTimeStageInput ? '0.01' : undefined}
                                                                                        min='0'
                                                                                        max={isTimeStageInput ? '9999' : (stage.type === 'fri' ? '60' : '50')}
                                                                                        data-delay={1000}
                                                                                        class='form-control form-control-sm py-0 px-1 text-center'
                                                                                        style={isTimeStageInput
                                                                                            ? 'width: 55px; min-width: 55px; max-width: 55px; font-size: 1.1rem; line-height: 1.2rem;'
                                                                                            : 'width: 40px; min-width: 40px; max-width: 40px; font-size: 1.1rem; line-height: 1.2rem;'}
                                                                                        size={isTimeStageInput ? '4' : '2'}
                                                                                        data-participant={participant.id}
                                                                                        data-idx={index}
                                                                                        data-id={targetResult?.id}
                                                                                        data-stage={stage.md5}
                                                                                        data-type={stage.type}
                                                                                        name='scoreTotal'
                                                                                        onFocus={this.handleFocus}
                                                                                        // onBlur={this.handleBlur}
                                                                                        onInput={this.updateResult}
                                                                                        value={targetResult?.scoreTotal || ''}
                                                                                        disabled={isAllowToEditScore ? '' : 'disabled'}
                                                                                        autoComplete='off'
                                                                                    />
                                                                                </> : <>{targetResult?.scoreTotal || <span class='text-muted'>-</span>}</>}
                                                                            </td>
                                                                        </>);
                                                                    })}
                                                                    <td class='text-center'>
                                                                        <span style='font-weight: 700;'>
                                                                            {(() => {
                                                                                const isTimeStageType = this.isTimeStage(stage.type);
                                                                                if (!isTimeStageType) {
                                                                                    return totalScore;
                                                                                }
                                                                                // Collect times only for valid field indices
                                                                                const fields = conventionResultStore.getParticipantFields(stage);
                                                                                const times = fields.map((field, index) => {
                                                                                    const result = participantResults.find(r => r.targetIndex === index);
                                                                                    return result?.scoreTotal || 0;
                                                                                }).filter(t => t > 0);
                                                                                if (times.length === 0) {
                                                                                    return '-';
                                                                                }
                                                                                // tidsoving: show average, tidskonk: show best (lowest) time
                                                                                if (stage.type === 'tidsoving') {
                                                                                    const avg = times.reduce((a, b) => a + b, 0) / times.length;
                                                                                    return avg.toFixed(2);
                                                                                } else if (stage.type === 'tidskonk') {
                                                                                    return Math.min(...times).toFixed(2);
                                                                                }
                                                                                return totalScore;
                                                                            })()}
                                                                        </span>
                                                                    </td>
                                                                    {(stage.type === 'nais' || stage.type === 'hurtig') ? <>
                                                                        <td class='text-center py-1'>
                                                                            {(() => {
                                                                                const mark = getOfficialMark(totalScore, participant.class, stage.type);
                                                                                if (mark) {
                                                                                    return (
                                                                                        <span
                                                                                            title={mark.title}
                                                                                            style={`
                                                                                                display: inline-block;
                                                                                                padding: 1px 4px;
                                                                                                border-radius: 3px;
                                                                                                font-weight: 600;
                                                                                                font-size: 0.7rem;
                                                                                                background-color: ${mark.color};
                                                                                                color: ${mark.color === '#C0C0C0' ? '#333' : '#fff'};
                                                                                                text-shadow: 1px 1px 1px rgba(0,0,0,0.2);
                                                                                            `}
                                                                                        >
                                                                                            {mark.label}
                                                                                        </span>
                                                                                    );
                                                                                }
                                                                                return <span class='text-muted' style='font-size: 0.65rem;'>-</span>;
                                                                            })()}
                                                                        </td>
                                                                    </> : <></>}
                                                                </tr>
                                                            </>);
                                                        })}
                                                    </tbody>
                                                </table>

                                                {isConventionAdmin && !focusModeActive && !stageDone && idx >= 0 && <>
                                                    <div class='text-muted mt-3 mb-1' style='font-size: 0.8rem;'>
                                                        <Text id='convention.admin-add-new-user'>Admin add new user to stage</Text>:
                                                    </div>

                                                    {isInThePast ? <>
                                                        <div class='d-flex text-muted' style='font-size: 0.85rem;'>
                                                            <Text id='convention.in-the-past'>Convention is in the past and closed.</Text>
                                                        </div>
                                                    </> : <>
                                                        <div>
                                                            {(() => {
                                                                // Only participants not yet on this stage, sorted by stage count then signup date
                                                                const missingParticipants = (convention.participants || []).filter(participant =>
                                                                    !participants?.some(p => p.id === participant.id)
                                                                ).sort((a, b) => {
                                                                    // Sort by stage count ascending, then by signup date ascending
                                                                    const stagesA = participantStages[a.id] || 0;
                                                                    const stagesB = participantStages[b.id] || 0;
                                                                    if (stagesA !== stagesB) return stagesA - stagesB;
                                                                    const dateA = a.signupDate ? new Date(a.signupDate).getTime() : 0;
                                                                    const dateB = b.signupDate ? new Date(b.signupDate).getTime() : 0;
                                                                    return dateA - dateB;
                                                                });

                                                                // Group by stage count so each group gets its own "N lag" header
                                                                const stageCountGroups = [];
                                                                missingParticipants.forEach(participant => {
                                                                    const stageCount = participantStages[participant.id] || 0;
                                                                    let group = stageCountGroups[stageCountGroups.length - 1];
                                                                    if (!group || group.stageCount !== stageCount) {
                                                                        group = { stageCount, participants: [] };
                                                                        stageCountGroups.push(group);
                                                                    }
                                                                    group.participants.push(participant);
                                                                });

                                                                return stageCountGroups.map(group => {
                                                                    const isUnassigned = group.stageCount === 0;
                                                                    return (
                                                                    <div class={`mb-2 ${isUnassigned ? 'p-2 rounded border border-success-subtle bg-success-subtle' : ''}`}>
                                                                        <div class={`border-bottom mb-1 pb-1 ${isUnassigned ? 'text-success-emphasis border-success-subtle' : 'text-body-secondary'}`} style={`font-size: ${isUnassigned ? '0.95rem' : '0.7rem'};`}>
                                                                            {isUnassigned && <i class='fa-solid fa-user-plus me-1' />}
                                                                            <span class={isUnassigned ? 'fw-bold' : 'fw-semibold text-body'}>
                                                                                <Text id='convention.stage-count-group' fields={{ count: group.stageCount }}>{group.stageCount} lag</Text>
                                                                            </span>
                                                                            <span class='ms-1 fst-italic'>
                                                                                {group.stageCount === 0
                                                                                    ? <Text id='convention.stage-count-group-desc-none'>– ikke satt på noe lag ennå</Text>
                                                                                    : <Text id='convention.stage-count-group-desc-some' fields={{ count: group.stageCount }}>– allerede på {group.stageCount} lag</Text>}
                                                                            </span>
                                                                        </div>
                                                                        <div class='d-flex flex-wrap gap-2'>
                                                                            {group.participants.map(participant => {
                                                                                // Check if signup date is today
                                                                                const signupDate = participant.signupDate ? new Date(participant.signupDate) : null;
                                                                                const today = new Date();
                                                                                const isToday = signupDate &&
                                                                                    signupDate.getDate() === today.getDate() &&
                                                                                    signupDate.getMonth() === today.getMonth() &&
                                                                                    signupDate.getFullYear() === today.getFullYear();

                                                                                const stageCount = participantStages[participant.id] || 0;
                                                                                const feeStarts = requiredStarts(convention, stageCount);
                                                                                const hasSignupProduct = convention.signupProduct > 0;
                                                                                const paidStartsCount = participant.paidStartsCount || 0;
                                                                                const fullyPaid = hasSignupProduct && feeStarts > 0 && paidStartsCount >= feeStarts;
                                                                                const partiallyPaid = hasSignupProduct && paidStartsCount > 0 && paidStartsCount < feeStarts;
                                                                                let paymentTitle = '';
                                                                                if (hasSignupProduct) {
                                                                                    paymentTitle = ` - ${paidStartsCount} av ${feeStarts} starter betalt`;
                                                                                }
                                                                                const paymentIcon = fullyPaid ? 'fa-circle-check text-success' : (partiallyPaid ? 'fa-coins text-info' : 'fa-clock text-warning');

                                                                                return (
                                                                                    <button
                                                                                        class='btn btn-outline-primary rounded-pill px-3 py-2'
                                                                                        style='font-size: 0.9rem; min-height: 40px;'
                                                                                        onClick={this.addParticipant}
                                                                                        data-id={participant.id}
                                                                                        data-md5={stage.md5}
                                                                                        title={`Legg til ${localUtil.displayName(participant)} - ${stageCount} lag${paymentTitle}`}
                                                                                    >
                                                                                        <i class='fa-solid fa-plus me-1' style='font-size: 0.7rem;' />
                                                                                        {hasSignupProduct && (
                                                                                            <i class={`fa-solid ${paymentIcon} me-1`} style='font-size: 0.7rem;' />
                                                                                        )}
                                                                                        {signupDate && (
                                                                                            <span class='text-muted me-1'>
                                                                                                {isToday ? util.isoTime(participant.signupDate) : `${signupDate.getDate()}.${signupDate.toLocaleString('nb-NO', { month: 'short' })} ${util.isoTime(participant.signupDate)}`}
                                                                                            </span>
                                                                                        )}
                                                                                        <span style='font-stretch: condensed;'>{localUtil.displayName(participant)}</span>
                                                                                        <span class='text-muted ms-1'>
                                                                                            ({hasSignupProduct ? `${paidStartsCount}/${feeStarts}` : stageCount})
                                                                                        </span>
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                    </>}
                                                </>}

                                            </div>
                                        </div>
                                    </>);
                                })}
                            </> : <></>}

                            {/* Quick «Ny <type>» buttons for admins. With Lag on the stevne the
                                big button repeats the last Lag's type and the small ones offer
                                the other common types; with no Lag yet the common types are
                                offered as equals. */}
                            {isConventionAdmin && !convention.hideStages && canAddRound && (
                                <div class='px-3 mt-3 mb-1'>
                                    {lastStage && (
                                        <button
                                            type='button'
                                            class='btn btn-lg btn-primary rounded-pill w-100'
                                            data-type={lastStage.type}
                                            onClick={this.newRoundLikeLast}
                                            disabled={this.state.creatingRound}
                                        >
                                            <i class={`fa-solid ${this.state.creatingRound ? 'fa-spinner fa-spin' : 'fa-plus'} me-2`} />
                                            <Text id='convention.new-round-like-last' fields={{ type: getStageTypeDisplay(lastStage.type) }}>New {getStageTypeDisplay(lastStage.type)}</Text>
                                        </button>
                                    )}
                                    <div class={`d-flex gap-2 ${lastStage ? 'mt-2' : ''}`}>
                                        {COMMON_STAGE_TYPES.filter(type => !lastStage || type !== lastStage.type).map(type => (
                                            <button
                                                key={type}
                                                type='button'
                                                class='btn btn-outline-primary rounded-pill flex-fill'
                                                style='min-height: 40px;'
                                                data-type={type}
                                                onClick={this.newRoundLikeLast}
                                                disabled={this.state.creatingRound}
                                            >
                                                <i class='fa-solid fa-plus me-2' />
                                                <Text id='convention.new-round-like-last' fields={{ type: getStageTypeDisplay(type) }}>New {getStageTypeDisplay(type)}</Text>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Megalink: the stevne has a Lag the live shots page can show
                                → offer the page before and during the stevne (it counts
                                down to the next Lag itself). The badge names the Lag that
                                is Pågår. Hidden once the stevne is over. */}
                            {!isInThePast && megalinkStages(convention).length > 0 && (
                                <div class='px-3 mt-3 mb-1'>
                                    <a
                                        href={`/tv/convention/${convention.id}/shots`}
                                        class='btn btn-lg btn-outline-primary rounded-pill w-100'
                                    >
                                        <i class='fa-duotone fa-bullseye me-2' />
                                        <Text id='convention.view-live-shots'>Watch the shooting live</Text>
                                        {runningMegalinkStages(convention).length > 0 && (
                                            <span class='badge text-bg-primary ms-2'>
                                                {runningMegalinkStages(convention).map(({ stage, idx }) => stage.name || `Lag ${idx + 1}`).join(', ')}
                                            </span>
                                        )}
                                    </a>
                                </div>
                            )}

                            {/* Lottery prizes + winners — shooters check here what they
                                won and claim it in the kiosk. Hidden in focus mode. */}
                            {!focusModeActive && (
                                <ConventionPrizes stores={this.props.stores} />
                            )}

                            {/* User-contributed event photos + "Add photo" — material for
                                the recap/summary feed post. Hidden in focus mode. */}
                            {!focusModeActive && (
                                <ConventionEventPhotos stores={this.props.stores} />
                            )}

                            {!focusModeActive && convention.stageStandImages?.some(image => image) && (
                                <div class='px-3 mt-3 mb-1'>
                                    <button
                                        type='button'
                                        class='btn btn-lg btn-outline-primary rounded-pill w-100'
                                        onClick={this.openCourseDrawer}
                                    >
                                        <i class='fa-duotone fa-route me-2' />
                                        <Text id='convention.view-course'>View the course</Text>
                                        <span class='badge text-bg-primary ms-2'>
                                            {convention.stageStandImages.filter(image => image).length}
                                        </span>
                                    </button>
                                </div>
                            )}

                            {focusModeAvailable && (
                                <div class='px-3 mt-3 mb-2'>
                                    <button
                                        type='button'
                                        class={`btn ${focusModeActive ? 'btn-primary' : 'btn-outline-primary'} rounded-pill w-100`}
                                        onClick={this.toggleFocusMode}
                                    >
                                        <i class={`fa-duotone ${focusModeActive ? 'fa-eye' : 'fa-eye-slash'} me-2`} />
                                        {focusModeActive
                                            ? <Text id='convention.show-all'>Show all</Text>
                                            : <Text id='convention.focus-mode'>Focus mode</Text>}
                                    </button>
                                </div>
                            )}

                            </>)}

                        </div>
                    </div>

                    {isAdminRole && !focusModeActive && (
                        <div class='d-flex flex-row justify-content-center gap-2 mt-4 mb-3 px-3'>
                            <button
                                type='button'
                                class='btn btn-outline-secondary rounded-pill'
                                onClick={this.openAdminCopyDrawer}
                            >
                                <i class='fa-duotone fa-clone me-2' />
                                <Text id='convention.admin-copy'>Copy</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-secondary rounded-pill'
                                onClick={this.openAdminFeltDrawer}
                            >
                                <i class='fa-duotone fa-bullseye-arrow me-2' />
                                <Text id='convention.admin-felt'>Felt</Text>
                            </button>
                        </div>
                    )}

                </div>
            </div>
            {isAdminRole && <RemoteCommandBar stores={this.props.stores} fixed={true} />}
        </>);
    }
}

export default ConventionView;
